import React, { useState, useCallback, useEffect } from 'react';
import {
  Box,
  Button,
  ChakraProvider,
  Container,
  Flex,
  FormControl,
  FormLabel,
  FormErrorMessage,
  Heading,
  HStack,
  Input,
  Select,
  Switch,
  Table,
  Tbody,
  Td,
  Text,
  Textarea,
  Th,
  Thead,
  Tr,
  VStack,
  useToast,
  IconButton,
  NumberInput,
  NumberInputField,
  Divider,
  Badge,
  extendTheme,
  Card,
  CardBody,
  CardHeader,
  Image,
  Checkbox,
  Spinner
} from '@chakra-ui/react';
import { DeleteIcon, AddIcon, DownloadIcon, AttachmentIcon, CopyIcon } from '@chakra-ui/icons';
import { FaSave, FaShare, FaWhatsapp, FaEnvelope, FaUndo } from 'react-icons/fa';
import * as XLSX from 'xlsx';
import { useSearchParams } from 'react-router-dom';

const theme = extendTheme({
  colors: {
    brand: {
      50: '#e3f2fd',
      100: '#bbdefb',
      500: '#2196F3',
      600: '#1976D2',
      700: '#1565C0',
    },
  },
});

const ProductOnboardingForm = ({ platform }) => {
  const [searchParams, setSearchParams] = useSearchParams();
  const fileIdFromUrl = searchParams.get('fileId');
  
  // Pre-set platform based on prop (from URL route)
  const [isShopify, setIsShopify] = useState(platform === 'shopify');
  const hidePlatformToggle = platform === 'shopify' || platform === 'non-shopify';
  const [formData, setFormData] = useState({
    brandName: '',
    taxInclusive: '',
  });
  const [currentFileId, setCurrentFileId] = useState(fileIdFromUrl || null);
  const [shareableLink, setShareableLink] = useState(null);
  const [isLoadingData, setIsLoadingData] = useState(false);
  const [updateExisting, setUpdateExisting] = useState(false);
  const [showShareSection, setShowShareSection] = useState(false);
  const [fileIdVerified, setFileIdVerified] = useState(false); // Track if fileId is verified from Google
  const [isGeneratingLink, setIsGeneratingLink] = useState(false); // Track if generating shareable link
  
  const [products, setProducts] = useState([{
    id: 1,
    productName: '',
    productLinks: '',
    description: '',
    category: '',
    subcategory: '',
    sku: '',
    inventoryAvailability: '',
    leadTime: '',
    price: '',
    quantity: '',
    size: '',
    color: '',
    pattern: '',
    weight: '',
    length: '',
    width: '',
    height: '',
    mainImage: '',
    additionalImages: '',
    notes: '',
    packagingType: '',
    // Product Details Section
    productDescription: '',
    styleFeature1: '',
    styleFeature2: '',
    styleFeature3: '',
    styleFeature4: '',
    styleFeature5: '',
    styleFeature6: '',
    materialComposition: '',
    careInstructions: '',
    additionalCareNotes: '',
    // Fit Section
    fitType: '',
    modelWearingSize: '',
    modelAgeRange: '',
    modelHeight: '',
    additionalFitNotes: '',
    // Bag-specific fields
    liningMaterial: '',
    hardwareFinish: '',
    handleType: '',
    handleStrapDrop: '',
    strapAdjustable: '',
    // Home Decor fields
    colorFinishOptions: '',
    origin: '',
    setContents: '',
    additionalInformation: '',
    limitedEditionInfo: '',
    frameMaterial: '',
    photoArtSizeCapacity: '',
    orientation: '',
    mountingType: '',
    // Candles & Fragrances fields
    scentFragrance: '',
    capacity: '',
    burningDuration: '',
    nonCandleProductType: '',
    usageInstructions: '',
    safetyWarnings: '',
    // Tableware / Dining fields
    tablewareCapacity: '',
    foodSafeCertification: ''
  }]);

  const toast = useToast();
  const [isSaving, setIsSaving] = useState(false);
  const [errors, setErrors] = useState({});
  
  // Product Onboarding Script URL - Enhanced script with shareable links
  const scriptUrl = 'https://script.google.com/macros/s/AKfycbx4_QPAa5W-CCCUlgpasRwOLJ3bUhou7bKB3xy2HiTPXcT4hfo_2zdMtoWfXMG_XSBcJQ/exec';
  
  // Load data from Google Sheets when fileId is in URL
  const loadDataFromGoogleSheets = useCallback(async (fileId) => {
    if (!fileId) return;
    
    setIsLoadingData(true);
    try {
      // Try to fetch data - Google Apps Script Web Apps may have CORS issues
      // Try CORS first, if it fails, show helpful error message
      let result = null;
      try {
        const response = await fetch(`${scriptUrl}?fileId=${fileId}`, {
          method: 'GET',
          mode: 'cors'
        });
        
        if (!response.ok) {
          throw new Error(`HTTP error! status: ${response.status}`);
        }
        
        result = await response.json();
      } catch (corsError) {
        // CORS failed - this happens when:
        // 1. Script is not deployed with "Anyone" access, OR
        // 2. Enhanced script not deployed yet
        console.error('CORS error loading data:', corsError);
        throw new Error('Cannot load data: CORS not enabled. Please deploy PRODUCT_ONBOARDING_SCRIPT_ENHANCED.js as Web App with "Anyone" access.');
      }
      
      if (result.success && result.products) {
        // Set form data
        setFormData({
          brandName: result.brandName || '',
          taxInclusive: result.taxInclusive || ''
        });
        
        // Parse products from the loaded data
        const loadedProducts = result.products.map((product, index) => ({
          id: index + 1,
          productName: product['Product Name'] || '',
          productLinks: product['Product Links'] || '',
          description: product['Description'] || '',
          category: product['Category'] || '',
          subcategory: product['Subcategory'] || '',
          sku: product['SKU/Product ID'] || '',
          inventoryAvailability: product['Inventory Availability'] || '',
          leadTime: product['Lead Time (Days)'] || '',
          price: product['Final Prices'] || product['Price (Local Currency)'] || '',
          quantity: product['Quantity in Stock'] || '',
          size: product['Size'] || '',
          color: product['Color'] || '',
          pattern: product['Pattern/Print'] || '',
          weight: product['Weight (kg/g)'] || product['Actual Weight (kg/g)'] || '',
          length: product['Length (cm)'] || '',
          width: product['Width (cm)'] || '',
          height: product['Height (cm)'] || '',
          mainImage: product['Main Image File Name'] || '',
          additionalImages: product['Additional Image File Names'] || '',
          notes: product['Notes'] || '',
          packagingType: product['Packaging Type'] || '',
          // Product Details Section
          productDescription: product['Product Description'] || '',
          styleFeature1: product['Style Feature 1'] || '',
          styleFeature2: product['Style Feature 2'] || '',
          styleFeature3: product['Style Feature 3'] || '',
          styleFeature4: product['Style Feature 4'] || '',
          styleFeature5: product['Style Feature 5'] || '',
          styleFeature6: product['Style Feature 6'] || '',
          materialComposition: product['Material Composition'] || '',
          careInstructions: product['Care Instructions'] || '',
          additionalCareNotes: product['Additional Care Notes'] || '',
          // Bag-specific fields
          liningMaterial: product['Lining Material'] || '',
          hardwareFinish: product['Hardware Finish'] || '',
          handleType: product['Handle Type'] || '',
          handleStrapDrop: product['Handle/Strap Drop'] || '',
          strapAdjustable: product['Strap - Adjustable?'] || '',
          // Home Decor fields
          colorFinishOptions: product['Color/Finish Options'] || '',
          origin: product['Origin'] || '',
          setContents: product['Set Contents'] || '',
          additionalInformation: product['Additional Information'] || '',
          limitedEditionInfo: product['Limited Edition Info'] || '',
          frameMaterial: product['Frame Material'] || '',
          photoArtSizeCapacity: product['Photo/Art Size Capacity'] || '',
          orientation: product['Orientation'] || '',
          mountingType: product['Mounting Type'] || '',
          // Candles & Fragrances fields
          scentFragrance: product['Scent/Fragrance'] || '',
          capacity: product['Capacity'] || '',
          burningDuration: product['Burning Duration'] || '',
          nonCandleProductType: product['Non-Candle Product Type'] || '',
          usageInstructions: product['Usage Instructions'] || '',
          safetyWarnings: product['Safety Warnings'] || '',
          // Tableware / Dining fields
          tablewareCapacity: product['Capacity'] || '',
          foodSafeCertification: product['Food Safe Certification'] || '',
          // Fit Section
          fitType: product['Fit Type'] || '',
          modelWearingSize: product['Model Wearing Size'] || '',
          modelAgeRange: product['Model Age Range'] || '',
          modelHeight: product['Model measurements (CM)'] || product['Model Height (cm)'] || '',
          additionalFitNotes: product['Additional Fit Notes'] || ''
        }));
        
        // Filter out empty products
        const validProducts = loadedProducts.filter(p => 
          p.productName || p.productLinks || p.category
        );
        
        if (validProducts.length > 0) {
          setProducts(validProducts);
        }
        
        setCurrentFileId(fileId);
        setUpdateExisting(true);
        setFileIdVerified(true); // Mark fileId as verified from Google - checkbox will appear
        // Generate shareable link when data is loaded from URL
        const baseUrl = window.location.origin;
        const platformPath = platform || (isShopify ? 'shopify' : 'non-shopify');
        const shareLink = `${baseUrl}/${platformPath}/product-onboarding?fileId=${fileId}`;
        setShareableLink(shareLink);
        // Don't auto-check checkbox - let user decide when to show share section
        
        toast({
          title: 'Data loaded',
          description: 'Form pre-filled with existing data',
          status: 'success',
          duration: 3000,
          isClosable: true,
        });
      }
    } catch (error) {
      console.error('Error loading data:', error);
      toast({
        title: 'Error loading data',
        description: 'Could not load data from Google Sheets',
        status: 'error',
        duration: 3000,
        isClosable: true,
      });
    } finally {
      setIsLoadingData(false);
    }
  }, [scriptUrl, toast]);

  // Validate required fields
  const validateForm = () => {
    const newErrors = {};

    // Brand Name is required
    if (!formData.brandName.trim()) {
      newErrors.brandName = 'Brand name is required';
    }

    // Tax Inclusive is required
    if (!formData.taxInclusive.trim()) {
      newErrors.taxInclusive = 'Tax Inclusive is required';
    }

    // Validate products
    products.forEach((product, index) => {
      if (isShopify) {
        if (!product.productLinks.trim()) {
          newErrors[`productLinks_${index}`] = 'Product links is required';
        }
      } else {
        if (!product.productName.trim()) {
          newErrors[`productName_${index}`] = 'Product name is required';
        }
      }

      if (!product.category.trim()) {
        newErrors[`category_${index}`] = 'Category is required';
      }

      if (!product.subcategory.trim()) {
        newErrors[`subcategory_${index}`] = 'Subcategory is required';
      }

      if (!product.price.trim()) {
        newErrors[`price_${index}`] = 'Price is required';
      }

      // Validate Product Details fields if category matches
      if (shouldShowProductDetailsFields(product.category)) {
        if (!product.productDescription.trim()) {
          newErrors[`productDescription_${index}`] = 'Product Description is required';
        }
        if (!product.styleFeature1.trim()) {
          newErrors[`styleFeature1_${index}`] = 'Style Feature 1 is required';
        }
        if (!product.styleFeature2.trim()) {
          newErrors[`styleFeature2_${index}`] = 'Style Feature 2 is required';
        }
        if (!product.styleFeature3.trim()) {
          newErrors[`styleFeature3_${index}`] = 'Style Feature 3 is required';
        }
        if (!product.materialComposition.trim()) {
          newErrors[`materialComposition_${index}`] = 'Material Composition is required';
        }
        if (!product.careInstructions.trim()) {
          newErrors[`careInstructions_${index}`] = 'Care Instructions is required';
        }
        
        // Bag-specific validation
        if (shouldShowBagFields(product.category)) {
          if (!product.hardwareFinish.trim()) {
            newErrors[`hardwareFinish_${index}`] = 'Hardware Finish is required';
          }
          if (!product.handleType.trim()) {
            newErrors[`handleType_${index}`] = 'Handle Type is required';
          }
          if (!product.strapAdjustable.trim()) {
            newErrors[`strapAdjustable_${index}`] = 'Strap - Adjustable? is required';
          }
        } else if (shouldShowHomeDecorFields(product.category)) {
          // Home Decor validation
          if (!product.colorFinishOptions.trim()) {
            newErrors[`colorFinishOptions_${index}`] = 'Color/Finish Options is required';
          }
          if (!product.origin.trim()) {
            newErrors[`origin_${index}`] = 'Origin is required';
          }
          if (!product.setContents.trim()) {
            newErrors[`setContents_${index}`] = 'Set Contents is required';
          }
        } else if (shouldShowCandlesFragrancesFields(product.category)) {
          // Candles & Fragrances validation
          if (!product.colorFinishOptions.trim()) {
            newErrors[`colorFinishOptions_${index}`] = 'Color/Finish Options is required';
          }
          if (!product.origin.trim()) {
            newErrors[`origin_${index}`] = 'Origin is required';
          }
          if (!product.safetyWarnings.trim()) {
            newErrors[`safetyWarnings_${index}`] = 'Safety Warnings is required';
          }
        } else if (shouldShowTablewareDiningFields(product.category)) {
          // Tableware / Dining validation
          if (!product.colorFinishOptions.trim()) {
            newErrors[`colorFinishOptions_${index}`] = 'Color/Finish Options is required';
          }
          if (!product.origin.trim()) {
            newErrors[`origin_${index}`] = 'Origin is required';
          }
          if (!product.safetyWarnings.trim()) {
            newErrors[`safetyWarnings_${index}`] = 'Safety Warnings is required';
          }
        } else {
          // Regular clothing validation
          if (!product.fitType.trim()) {
            newErrors[`fitType_${index}`] = 'Fit Type is required';
          }
        }
      }
    });

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  // Save to localStorage
  const saveToLocalStorage = useCallback(() => {
    const dataToSave = {
      formData,
      products,
      isShopify,
      savedAt: new Date().toISOString()
    };
    localStorage.setItem('brandOnboardingData', JSON.stringify(dataToSave));
  }, [formData, products, isShopify]);

  // Load from localStorage
  const loadFromLocalStorage = useCallback(() => {
    try {
      const savedData = localStorage.getItem('brandOnboardingData');
      if (savedData) {
        const parsed = JSON.parse(savedData);
        if (parsed.formData) setFormData(parsed.formData);
        if (parsed.products && parsed.products.length > 0) setProducts(parsed.products);
        // Only restore isShopify from localStorage if we're NOT on a platform-specific route
        // If we're on a platform-specific route, respect the platform prop instead
        if (parsed.isShopify !== undefined && !platform) {
          setIsShopify(parsed.isShopify);
        }
      }
    } catch (error) {
      console.error('Error loading from localStorage:', error);
    }
  }, [platform]);
  
  // Sync isShopify with platform prop when it changes (for route-based platform switching)
  useEffect(() => {
    if (platform === 'shopify' || platform === 'non-shopify') {
      setIsShopify(platform === 'shopify');
    }
  }, [platform]);

  // Load data on mount if fileId is in URL (only on initial mount, not when fileId changes after save)
  useEffect(() => {
    if (fileIdFromUrl) {
      // Only load if fileId is in URL and we haven't loaded it yet
      // This handles shareable links - when someone opens a link with fileId
      loadDataFromGoogleSheets(fileIdFromUrl);
    } else if (!fileIdFromUrl && !currentFileId) {
      // Only load from localStorage if no fileId in URL and no current fileId
      loadFromLocalStorage();
    }
  }, []); // Only run on mount to prevent reloading after save

  // Auto-save to localStorage when data changes (debounced)
  useEffect(() => {
    const timer = setTimeout(() => {
      saveToLocalStorage();
    }, 1000); // Save 1 second after last change

    return () => clearTimeout(timer);
  }, [formData, products, isShopify, saveToLocalStorage]);

  const categories = {
    'Dresses': [
      'Maxi Dresses',
      'Midi Dresses',
      'Mini Dresses',
      'Modest Dresses',
      'Evening Dresses',
      'Day Dresses',
      'Occasion Dresses',
      'Kaftan & Abayas'
    ],
    'Tops': [
      'Blouses & Shirts',
      'T-shirts & Tanks',
      'Sweaters & Knitwear',
      'Bodysuits',
      'Vests'
    ],
    'Bottoms': [
      'Jeans',
      'Trousers & Pants',
      'Skirts',
      'Shorts'
    ],
    'OUTERWEAR': [
      'Blazers & Jackets',
      'Coats',
      'Kimonos',
      'Cardigans'
    ],
    'Sets & Co-ords': [
      'Matching Sets'
    ],
    'Jumpsuits': [
      'Jumpsuits'
    ],
    'Activewear': [
      'Sports Leggings',
      'Sports Pants',
      'Sports Shorts',
      'Sports Jumpsuits',
      'Sports Bras',
      'Sports Tops',
      'Sports Tanks',
      'Sports Sweaters / Jackets',
      'Sports Dresses / Skirts'
    ],
    'Swimwear & Beachwear': [
      'One-piece Swimsuits',
      'Bikinis',
      'Cover-ups & Kaftans',
      'Beach Dresses',
      'Swim Accessories'
    ],
    'Nightwear': [
      'Sleepwear Sets',
      'Night Dresses',
      'Robes'
    ],
    'Lounge wear': [
      'Hoodie & joggers',
      'Hoodie',
      'Joggers',
      'Shorts'
    ],
    'Girls (2-16 years)': [
      'Dresses',
      'Tops & T-shirts',
      'Pants & Leggings',
      'Outerwear & Jackets',
      'Traditional Wear',
      'Party & Occasion Wear',
      'Sleepwear & Loungewear',
      'T-shirt & Shorts Set',
      'Tracksuit Set',
      'Sports Bras',
      'Sports Legging',
      'Sports Tops & Tanks',
      'Skirts & Shorts',
      'Sports Skirt & Shorts'
    ],
    'Boys (2-16 years)': [
      'Shirts & Polos',
      'T-shirts & Tops',
      'Pants & Shorts',
      'Outerwear & Jackets',
      'Traditional Wear',
      'Party & Occasion Wear',
      'Sleepwear & Loungewear',
      'T-shirt & Shorts Set',
      'Tracksuit Set'
    ],
    'Unisex (2-16 years)': [
      'Tops & T-shirts',
      'Pants & Leggings',
      'Outerwear & Jackets',
      'Sleepwear & Loungewear',
      'T-shirt & Shorts Set',
      'Tracksuit Set'
    ],
    'Baby (0-24 months)': [
      'Bodysuits & Rompers',
      'Dresses (Girls)',
      'Shirts & Pants (Boys)',
      'Outerwear',
      'Sleep & Play',
      'Traditional Baby Wear'
    ],
    'Kids Accessories': [
      'Hair Accessories',
      'Small Bags & Backpacks',
      'Hats & Caps',
      'Jewelry (Safe for Kids)',
      'Scarves & Wraps',
      'Socks'
    ],
    'Kids Swimwear': [
      'Girls Swimwear',
      'Boys Swim Shorts',
      'Baby Swimwear',
      'Swim Accessories'
    ],
    'Bags': [
      'Handbags',
      'Clutches',
      'Totes',
      'Crossbody Bags',
      'Evening Bags',
      'Pouches'
    ],
    'Jewelry': [
      'Necklaces',
      'Earrings',
      'Bracelets',
      'Rings',
      'Statement Pieces'
    ],
    'Scarves & Wraps': [
      'Silk Scarves',
      'Hijabs',
      'Pashminas',
      'Wraps'
    ],
    'Other Accessories': [
      'Belts',
      'Hair Accessories',
      'Sunglasses',
      'Socks'
    ],
    'Home Decor': [
      'Sculptures & Figurines',
      'Decorative Bowls / Centerpieces',
      'Trays',
      'Picture Frames',
      'Vases',
      'Jewellery Boxes & Trays',
      'Coasters',
      'Mirrors',
      'Small Storage / Trinket Boxes',
      'Towels',
      'Cushions'
    ],
    'Tableware / Dining': [
      'Tableware',
      'Glassware',
      'Serveware',
      'Cutlery'
    ],
    'Candles & Fragrances': [
      'Candles',
      'Candle Holders',
      'Diffusers / Room Sprays',
      'Bukhoor & Oud',
      'Home Fragrance Sets'
    ],
    'Games & Leisure': [
      'Board Games',
      'Playing Cards',
      'Puzzles',
      'Game Sets / Decorative Games',
      'Books'
    ]
  };

  // Helper function to check if category should show Product Details and Fit sections
  const shouldShowProductDetailsFields = (category) => {
    const categoriesWithDetails = [
      'Dresses',
      'Tops',
      'Bottoms',
      'OUTERWEAR',
      'Sets & Co-ords',
      'Jumpsuits',
      'Activewear',
      'Swimwear & Beachwear',
      'Nightwear',
      'Lounge wear',
      'Girls (2-16 years)',
      'Boys (2-16 years)',
      'Unisex (2-16 years)',
      'Baby (0-24 months)',
      'Kids Accessories',
      'Kids Swimwear',
      'Bags',
      'Home Decor',
      'Candles & Fragrances',
      'Tableware / Dining'
    ];
    return categoriesWithDetails.includes(category);
  };

  // Helper function to check if category should show Bag-specific fields
  const shouldShowBagFields = (category) => {
    return category === 'Bags' || category === 'Kids Accessories';
  };

  // Helper function to check if category should show Home Decor fields
  const shouldShowHomeDecorFields = (category) => {
    return category === 'Home Decor';
  };

  // Helper function to check if category should show Candles & Fragrances fields
  const shouldShowCandlesFragrancesFields = (category) => {
    return category === 'Candles & Fragrances';
  };

  // Helper function to check if category should show Tableware / Dining fields
  const shouldShowTablewareDiningFields = (category) => {
    return category === 'Tableware / Dining';
  };

  // Helper function to get size options based on category and subcategory
  const getSizeOptions = (category, subcategory = '') => {
    // Adult clothing categories
    const adultCategories = ['Dresses', 'Tops', 'OUTERWEAR', 'Sets & Co-ords', 'Jumpsuits', 'Activewear', 'Swimwear & Beachwear', 'Nightwear', 'Lounge wear'];
    // Kids categories
    const kidsCategories = ['Girls (2-16 years)', 'Boys (2-16 years)', 'Unisex (2-16 years)'];
    // Baby category
    const babyCategory = 'Baby (0-24 months)';
    // Home categories (with materials)
    const homeCategoriesWithMaterials = ['Home Decor', 'Tableware / Dining', 'Candles & Fragrances'];
    // Games & Leisure has no size/color/pattern options

    if (adultCategories.includes(category)) {
      return [
        'XS (e.g. EU 34, 36)',
        'S (e.g. EU 36, 38)',
        'M (e.g. EU 38,40)',
        'L (e.g. EU 40, 42)',
        'XL (e.g. EUR 42,44)',
        'One Size'
      ];
    } else if (category === 'Bottoms') {
      // Bottoms also have waist sizes
      return [
        'XS (e.g. EU 34, 36)',
        'S (e.g. EU 36, 38)',
        'M (e.g. EU 38,40)',
        'L (e.g. EU 40, 42)',
        'XL (e.g. EUR 42,44)',
        'One Size',
        '24" waist',
        '25" waist',
        '26" waist',
        '27" waist',
        '28" waist',
        '29" waist',
        '30" waist',
        '31" waist',
        '32" waist'
      ];
    } else if (kidsCategories.includes(category)) {
      return [
        'Toddler: 2T, 3T, 4T',
        'Kids: 4, 5, 6, 7, 8, 9, 10, 11, 12'
      ];
    } else if (category === babyCategory) {
      return [
        '0-3M',
        '3-6M',
        '6-9M',
        '9-12M',
        '12-18M',
        '18-24M'
      ];
    } else if (category === 'Other Accessories') {
      // Check subcategory for specific accessory types
      if (subcategory === 'Belts') {
        return [
          'Waist belts',
          'Chain belts',
          'Leather belts',
          'Fabric belts'
        ];
      } else if (subcategory === 'Hair Accessories') {
        return [
          'Hair clips',
          'Headbands',
          'Hair combs',
          'Scrunchies'
        ];
      } else if (subcategory === 'Sunglasses') {
        return [
          'Aviator',
          'Cat-eye',
          'Round',
          'Oversized',
          'Square',
          'Rectangle'
        ];
      } else if (subcategory === 'Socks') {
        return [
          'XS',
          'S',
          'M',
          'L',
          'XL',
          'One Size'
        ];
      } else if (!subcategory) {
        // No subcategory selected yet - return empty or default
        return ['One Size'];
      }
    } else if (homeCategoriesWithMaterials.includes(category)) {
      // For home decor items - material based (from images: combined entries)
      return [
        'Ceramic',
        'Porcelain',
        'Glass',
        'Metal (Brass / Stainless Steel / Gold-tone / Silver-tone)',
        'Marble / Stone Wood',
        'Leather Fabric / Linen',
        'Wax (candles)',
        'Crystal / Acrylic',
        'Resin',
        'Oud Wood'
      ];
    } else if (category === 'Games & Leisure') {
      // Games & Leisure has no size options
      return [];
    }
    
    // Default for accessories (Bags, Jewelry, Scarves & Wraps) and other categories
    if (category === 'Bags' || category === 'Jewelry' || category === 'Scarves & Wraps' || category === 'Kids Accessories' || category === 'Kids Swimwear') {
      return ['One Size'];
    }
    
    // Default fallback
    return [
      'One Size',
      'XS',
      'S',
      'M',
      'L',
      'XL'
    ];
  };

  // Helper function to get color options based on category and subcategory
  const getColorOptions = (category, subcategory = '') => {
    // Kids and baby categories have Multicolour
    const kidsBabyCategories = ['Girls (2-16 years)', 'Boys (2-16 years)', 'Unisex (2-16 years)', 'Baby (0-24 months)', 'Kids Accessories', 'Kids Swimwear'];
    // Home categories have different colors (excluding Games & Leisure)
    const homeCategoriesWithColors = ['Home Decor', 'Tableware / Dining', 'Candles & Fragrances'];
    // Adult clothing categories
    const adultClothingCategories = ['Dresses', 'Tops', 'Bottoms', 'OUTERWEAR', 'Sets & Co-ords', 'Jumpsuits', 'Activewear', 'Swimwear & Beachwear', 'Nightwear', 'Lounge wear'];

    if (kidsBabyCategories.includes(category)) {
      return [
        'Beige/Nude',
        'Black',
        'Blue',
        'Brown',
        'Gold',
        'Green',
        'Grey',
        'Khaki',
        'Multicolour',
        'Navy',
        'Orange',
        'Pink',
        'Purple',
        'Red',
        'Rose Gold',
        'Silver',
        'Tan',
        'Turquoise',
        'White',
        'Yellow'
      ];
    } else if (homeCategoriesWithColors.includes(category)) {
      return [
        'Black',
        'Blue',
        'Brown',
        'Burgundy',
        'Cream',
        'Gold',
        'Grey',
        'Green',
        'Ivory',
        'Metallic',
        'Multi / Multicolour',
        'Neutrals',
        'Off‑white',
        'Orange',
        'Pink',
        'Purple',
        'Red',
        'Rose Gold',
        'Silver',
        'Tan',
        'White',
        'Yellow'
      ];
    } else if (category === 'Bags') {
      // Bags have specific colors from CSV
      return [
        'Black',
        'Blue',
        'Brown',
        'Burgundy',
        'Gold',
        'Green',
        'Grey',
        'Metallic',
        'Multicolour',
        'Neutral',
        'Orange',
        'Pink',
        'Purple',
        'Red',
        'Rose Gold',
        'Silver',
        'Tan',
        'White',
        'Yellow'
      ];
    } else if (category === 'Other Accessories') {
      // Other Accessories (Belts, Hair Accessories, Sunglasses) have specific colors
      return [
        'Black',
        'Blue',
        'Brown',
        'Burgundy',
        'Cream',
        'Gold',
        'Grey',
        'Green',
        'Ivory',
        'Metallic',
        'Multicolour',
        'Neutrals',
        'Off‑white',
        'Orange',
        'Pink',
        'Purple',
        'Red',
        'Rose Gold',
        'Silver',
        'Tan',
        'White',
        'Yellow'
      ];
    } else if (category === 'Scarves & Wraps') {
      // Scarves & Wraps have specific colors
      return [
        'Black',
        'Blue',
        'Brown',
        'Burgundy',
        'Cream',
        'Gold',
        'Grey',
        'Green',
        'Ivory',
        'Metallic',
        'Multi / Multicolour',
        'Neutrals',
        'Off‑white',
        'Orange',
        'Pink',
        'Purple',
        'Red',
        'Rose Gold',
        'Silver',
        'Tan',
        'White',
        'Yellow'
      ];
    } else if (adultClothingCategories.includes(category)) {
      // Adult clothing categories
      return [
        'Beige/Nude',
        'Black',
        'Blue',
        'Brown',
        'Gold',
        'Green',
        'Grey',
        'Khaki',
        'Navy',
        'Orange',
        'Pink',
        'Purple',
        'Red',
        'Rose Gold',
        'Silver',
        'Tan',
        'Turquoise',
        'White',
        'Yellow'
      ];
    } else if (category === 'Games & Leisure') {
      // Games & Leisure has no color options
      return [];
    } else {
      // Default for Jewelry and other categories
      return [
        'Black',
        'Blue',
        'Brown',
        'Gold',
        'Green',
        'Grey',
        'Metallic',
        'Multicolour',
        'Orange',
        'Pink',
        'Purple',
        'Red',
        'Rose Gold',
        'Silver',
        'Tan',
        'White',
        'Yellow'
      ];
    }
  };

  // Helper function to get pattern options
  const getPatternOptions = (category = '') => {
    // Games & Leisure has no pattern options
    if (category === 'Games & Leisure') {
      return [];
    }
    return [
      'Floral',
      'Geometric',
      'Stripes',
      'Abstract',
      'Traditional Motifs',
      'Animal Print'
    ];
  };

  const handleFormChange = (field, value) => {
    setFormData(prev => ({ ...prev, [field]: value }));
    // Clear error when field is filled
    if (errors[field] && value.trim()) {
      setErrors(prev => {
        const newErrors = { ...prev };
        delete newErrors[field];
        return newErrors;
      });
    }
  };

  const handleProductChange = (index, field, value) => {
    setProducts(prev => prev.map((product, i) => 
      i === index ? { ...product, [field]: value } : product
    ));
    // Clear error when field is filled
    const errorKey = `${field}_${index}`;
    if (errors[errorKey] && value.trim()) {
      setErrors(prev => {
        const newErrors = { ...prev };
        delete newErrors[errorKey];
        return newErrors;
      });
    }
  };

  const addProduct = () => {
    const newProduct = {
      id: products.length + 1,
      productName: '',
      productLinks: '',
      description: '',
      category: '',
      subcategory: '',
      sku: '',
      inventoryAvailability: '',
      leadTime: '',
      price: '',
      quantity: '',
      size: '',
      color: '',
      pattern: '',
      weight: '',
      length: '',
      width: '',
      height: '',
      mainImage: '',
      additionalImages: '',
      notes: '',
      packagingType: '',
      // Product Details Section
      productDescription: '',
      styleFeature1: '',
      styleFeature2: '',
      styleFeature3: '',
      styleFeature4: '',
      styleFeature5: '',
      styleFeature6: '',
      materialComposition: '',
      careInstructions: '',
      additionalCareNotes: '',
      // Fit Section
      fitType: '',
      modelWearingSize: '',
      modelAgeRange: '',
      modelHeight: '',
      additionalFitNotes: '',
      // Bag-specific fields
      liningMaterial: '',
      hardwareFinish: '',
      handleType: '',
      handleStrapDrop: '',
      strapAdjustable: ''
    };
    setProducts(prev => [...prev, newProduct]);
  };

  const removeProduct = (index) => {
    if (products.length > 1) {
      setProducts(prev => prev.filter((_, i) => i !== index));
    } else {
      toast({
        title: 'Cannot remove the last row',
        description: 'Please add a new row first.',
        status: 'warning',
        duration: 3000,
        isClosable: true,
      });
    }
  };

  const downloadExcel = () => {
    const baseHeaders = isShopify 
      ? ['Product Links', 'Category', 'Subcategory', 'SKU/Product ID', 'Inventory Availability', 'Lead Time (Days)', 'Final Prices', 'Quantity in Stock', 'Weight (kg/g)', 'Length (cm)', 'Width (cm)', 'Height (cm)', 'Packaging Type']
      : ['Product Name', 'Description', 'Category', 'Subcategory', 'SKU/Product ID', 'Inventory Availability', 'Lead Time (Days)', 'Price (Local Currency)', 'Quantity in Stock', 'Size', 'Color', 'Pattern/Print', 'Actual Weight (kg/g)', 'Length (cm)', 'Width (cm)', 'Height (cm)', 'Main Image File Name', 'Additional Image File Names', 'Notes', 'Packaging Type'];

    // Add conditional headers if any product has matching category
    const hasBagCategory = products.some(p => shouldShowBagFields(p.category));
    const hasHomeDecorCategory = products.some(p => shouldShowHomeDecorFields(p.category));
    const hasCandlesFragrancesCategory = products.some(p => shouldShowCandlesFragrancesFields(p.category));
    const hasTablewareDiningCategory = products.some(p => shouldShowTablewareDiningFields(p.category));
    const productDetailsHeaders = products.some(p => shouldShowProductDetailsFields(p.category)) ? (
      hasBagCategory ? [
        'Product Description', 'Style Feature 1', 'Style Feature 2', 'Style Feature 3', 'Style Feature 4', 'Style Feature 5', 'Style Feature 6',
        'Material Composition', 'Lining Material', 'Hardware Finish', 'Care Instructions', 'Additional Care Notes',
        'Handle Type', 'Handle/Strap Drop', 'Strap - Adjustable?'
      ] : hasHomeDecorCategory ? [
        'Product Description', 'Style Feature 1', 'Style Feature 2', 'Style Feature 3', 'Style Feature 4', 'Style Feature 5', 'Style Feature 6',
        'Material Composition', 'Care Instructions', 'Additional Care Notes',
        'Color/Finish Options', 'Origin', 'Set Contents', 'Additional Information', 'Limited Edition Info',
        'Frame Material', 'Photo/Art Size Capacity', 'Orientation', 'Mounting Type'
      ] : hasCandlesFragrancesCategory ? [
        'Product Description', 'Style Feature 1', 'Style Feature 2', 'Style Feature 3', 'Style Feature 4', 'Style Feature 5', 'Style Feature 6',
        'Material Composition', 'Care Instructions', 'Additional Care Notes',
        'Scent/Fragrance', 'Color/Finish Options', 'Origin', 'Capacity', 'Set Contents',
        'Burning Duration', 'Non-Candle Product Type', 'Usage Instructions', 'Safety Warnings'
      ] : hasTablewareDiningCategory ? [
        'Product Description', 'Style Feature 1', 'Style Feature 2', 'Style Feature 3', 'Style Feature 4', 'Style Feature 5', 'Style Feature 6',
        'Material Composition', 'Care Instructions', 'Additional Care Notes',
        'Color/Finish Options', 'Origin', 'Capacity', 'Set Contents', 'Safety Warnings', 'Food Safe Certification'
      ] : [
        'Product Description', 'Style Feature 1', 'Style Feature 2', 'Style Feature 3', 'Style Feature 4', 'Style Feature 5', 'Style Feature 6',
        'Material Composition', 'Care Instructions', 'Additional Care Notes',
        'Fit Type', 'Model Wearing Size', 'Model Age Range', 'Model measurements (CM)', 'Additional Fit Notes'
      ]
    ) : [];

    const headers = [...baseHeaders, ...productDetailsHeaders];
    const formHeaders = ['Brand Name', 'Tax Inclusive'];
    const headersWithForm = formHeaders.concat(headers);
    const worksheetData = [headersWithForm];

    products.forEach(product => {
      const formValues = [formData.brandName, formData.taxInclusive];

      const baseProductData = isShopify 
        ? [product.productLinks, product.category, product.subcategory, product.sku, product.inventoryAvailability, product.leadTime, product.price, product.quantity, product.weight, product.length, product.width, product.height, product.packagingType]
        : [product.productName, product.description, product.category, product.subcategory, product.sku, product.inventoryAvailability, product.leadTime, product.price, product.quantity, product.size, product.color, product.pattern, product.weight, product.length, product.width, product.height, product.mainImage, product.additionalImages, product.notes, product.packagingType];

      // Add conditional product details data
      const productDetailsData = products.some(p => shouldShowProductDetailsFields(p.category)) ? (
        shouldShowBagFields(product.category) ? [
          product.productDescription || '',
          product.styleFeature1 || '',
          product.styleFeature2 || '',
          product.styleFeature3 || '',
          product.styleFeature4 || '',
          product.styleFeature5 || '',
          product.styleFeature6 || '',
          product.materialComposition || '',
          product.liningMaterial || '',
          product.hardwareFinish || '',
          product.careInstructions || '',
          product.additionalCareNotes || '',
          product.handleType || '',
          product.handleStrapDrop || '',
          product.strapAdjustable || ''
        ] : shouldShowHomeDecorFields(product.category) ? [
          product.productDescription || '',
          product.styleFeature1 || '',
          product.styleFeature2 || '',
          product.styleFeature3 || '',
          product.styleFeature4 || '',
          product.styleFeature5 || '',
          product.styleFeature6 || '',
          product.materialComposition || '',
          product.careInstructions || '',
          product.additionalCareNotes || '',
          product.colorFinishOptions || '',
          product.origin || '',
          product.setContents || '',
          product.additionalInformation || '',
          product.limitedEditionInfo || '',
          product.frameMaterial || '',
          product.photoArtSizeCapacity || '',
          product.orientation || '',
          product.mountingType || ''
        ] : shouldShowCandlesFragrancesFields(product.category) ? [
          product.productDescription || '',
          product.styleFeature1 || '',
          product.styleFeature2 || '',
          product.styleFeature3 || '',
          product.styleFeature4 || '',
          product.styleFeature5 || '',
          product.styleFeature6 || '',
          product.materialComposition || '',
          product.careInstructions || '',
          product.additionalCareNotes || '',
          product.scentFragrance || '',
          product.colorFinishOptions || '',
          product.origin || '',
          product.capacity || '',
          product.setContents || '',
          product.burningDuration || '',
          product.nonCandleProductType || '',
          product.usageInstructions || '',
          product.safetyWarnings || ''
        ] : shouldShowTablewareDiningFields(product.category) ? [
          product.productDescription || '',
          product.styleFeature1 || '',
          product.styleFeature2 || '',
          product.styleFeature3 || '',
          product.styleFeature4 || '',
          product.styleFeature5 || '',
          product.styleFeature6 || '',
          product.materialComposition || '',
          product.careInstructions || '',
          product.additionalCareNotes || '',
          product.colorFinishOptions || '',
          product.origin || '',
          product.tablewareCapacity || '',
          product.setContents || '',
          product.safetyWarnings || '',
          product.foodSafeCertification || ''
        ] : [
          product.productDescription || '',
          product.styleFeature1 || '',
          product.styleFeature2 || '',
          product.styleFeature3 || '',
          product.styleFeature4 || '',
          product.styleFeature5 || '',
          product.styleFeature6 || '',
          product.materialComposition || '',
          product.careInstructions || '',
          product.additionalCareNotes || '',
          product.fitType || '',
          product.modelWearingSize || '',
          product.modelAgeRange || '',
          product.modelHeight || '',
          product.additionalFitNotes || ''
        ]
      ) : [];

      const productData = [...baseProductData, ...productDetailsData];
      worksheetData.push([...formValues, ...productData]);
    });

    const wb = XLSX.utils.book_new();
    const ws = XLSX.utils.aoa_to_sheet(worksheetData);

    const colWidths = headersWithForm.map(h => ({ wch: Math.max(h.length, 15) }));
    ws['!cols'] = colWidths;

    XLSX.utils.book_append_sheet(wb, ws, 'Products');
    XLSX.writeFile(wb, 'Brand_Onboarding_Checklist.xlsx');

    toast({
      title: 'Excel file downloaded',
      description: 'Your data has been exported successfully.',
      status: 'success',
      duration: 3000,
      isClosable: true,
    });
  };

  // Reset form to empty state
  const resetForm = useCallback(() => {
    // Reset form data to initial state
    setFormData({
      brandName: '',
      taxInclusive: '',
    });
    
    // Reset products to initial state (one empty product)
    setProducts([{
      id: 1,
      productName: '',
      productLinks: '',
      description: '',
      category: '',
      subcategory: '',
      sku: '',
      inventoryAvailability: '',
      leadTime: '',
      price: '',
      quantity: '',
      size: '',
      color: '',
      pattern: '',
      weight: '',
      length: '',
      width: '',
      height: '',
      mainImage: '',
      additionalImages: '',
      notes: '',
      packagingType: '',
      // Product Details Section
      productDescription: '',
      styleFeature1: '',
      styleFeature2: '',
      styleFeature3: '',
      styleFeature4: '',
      styleFeature5: '',
      styleFeature6: '',
      materialComposition: '',
      careInstructions: '',
      additionalCareNotes: '',
      // Fit Section
      fitType: '',
      modelWearingSize: '',
      modelAgeRange: '',
      modelHeight: '',
      additionalFitNotes: '',
    // Bag-specific fields
    liningMaterial: '',
    hardwareFinish: '',
    handleType: '',
    handleStrapDrop: '',
    strapAdjustable: '',
    // Home Decor fields
    colorFinishOptions: '',
    origin: '',
    setContents: '',
    additionalInformation: '',
    limitedEditionInfo: '',
    frameMaterial: '',
    photoArtSizeCapacity: '',
    orientation: '',
    mountingType: '',
    // Candles & Fragrances fields
    scentFragrance: '',
    capacity: '',
    burningDuration: '',
    nonCandleProductType: '',
    usageInstructions: '',
    safetyWarnings: '',
    // Tableware / Dining fields
    tablewareCapacity: '',
    foodSafeCertification: ''
    }]);
    
    // Reset isShopify to match current platform prop (important for platform switching)
    setIsShopify(platform === 'shopify');
    
    // Clear all related state
    setCurrentFileId(null);
    setShareableLink(null);
    setUpdateExisting(false);
    setShowShareSection(false);
    setFileIdVerified(false);
    setIsGeneratingLink(false);
    setErrors({});
    
    // Clear ALL localStorage entries related to this form
    // Remove the specific key
    localStorage.removeItem('brandOnboardingData');
    
    // Also clear any other potential localStorage keys that might exist
    // This ensures a complete reset
    Object.keys(localStorage).forEach(key => {
      if (key.includes('brandOnboarding') || key.includes('productOnboarding')) {
        localStorage.removeItem(key);
      }
    });
    
    // Remove fileId from URL params
    setSearchParams({});
    
    // Show success toast
    toast({
      title: 'Form reset',
      description: 'All form data and localStorage cleared successfully',
      status: 'success',
      duration: 3000,
      isClosable: true,
    });
  }, [platform, toast, setSearchParams]);

  const saveToGoogleSheets = async () => {
    console.log('💾 saveToGoogleSheets called');
    console.log('📋 Form data:', { brandName: formData.brandName, taxInclusive: formData.taxInclusive });
    console.log('📦 Products count:', products.length);
    
    // Validate required fields
    if (!validateForm()) {
      console.log('❌ Validation failed');
      toast({
        title: 'Validation Error',
        description: 'Please fill in all required fields before saving.',
        status: 'error',
        duration: 4000,
        isClosable: true,
      });
      return;
    }

    console.log('✅ Validation passed, starting save...');
    setIsSaving(true);

    try {
      const baseHeaders = isShopify 
        ? ['Product Links', 'Category', 'Subcategory', 'SKU/Product ID', 'Inventory Availability', 'Lead Time (Days)', 'Final Prices', 'Quantity in Stock', 'Weight (kg/g)', 'Length (cm)', 'Width (cm)', 'Height (cm)', 'Packaging Type']
        : ['Product Name', 'Description', 'Category', 'Subcategory', 'SKU/Product ID', 'Inventory Availability', 'Lead Time (Days)', 'Price (Local Currency)', 'Quantity in Stock', 'Size', 'Color', 'Pattern/Print', 'Actual Weight (kg/g)', 'Length (cm)', 'Width (cm)', 'Height (cm)', 'Main Image File Name', 'Additional Image File Names', 'Notes', 'Packaging Type'];

      // Add conditional headers if any product has matching category
      const hasBagCategory = products.some(p => shouldShowBagFields(p.category));
      const hasHomeDecorCategory = products.some(p => shouldShowHomeDecorFields(p.category));
      const hasCandlesFragrancesCategory = products.some(p => shouldShowCandlesFragrancesFields(p.category));
      const hasTablewareDiningCategory = products.some(p => shouldShowTablewareDiningFields(p.category));
      const productDetailsHeaders = products.some(p => shouldShowProductDetailsFields(p.category)) ? (
        hasBagCategory ? [
          'Product Description', 'Style Feature 1', 'Style Feature 2', 'Style Feature 3', 'Style Feature 4', 'Style Feature 5', 'Style Feature 6',
          'Material Composition', 'Lining Material', 'Hardware Finish', 'Care Instructions', 'Additional Care Notes',
          'Handle Type', 'Handle/Strap Drop', 'Strap - Adjustable?'
        ] : hasHomeDecorCategory ? [
          'Product Description', 'Style Feature 1', 'Style Feature 2', 'Style Feature 3', 'Style Feature 4', 'Style Feature 5', 'Style Feature 6',
          'Material Composition', 'Care Instructions', 'Additional Care Notes',
          'Color/Finish Options', 'Origin', 'Set Contents', 'Additional Information', 'Limited Edition Info',
          'Frame Material', 'Photo/Art Size Capacity', 'Orientation', 'Mounting Type'
        ] : hasCandlesFragrancesCategory ? [
          'Product Description', 'Style Feature 1', 'Style Feature 2', 'Style Feature 3', 'Style Feature 4', 'Style Feature 5', 'Style Feature 6',
          'Material Composition', 'Care Instructions', 'Additional Care Notes',
          'Scent/Fragrance', 'Color/Finish Options', 'Origin', 'Capacity', 'Set Contents',
          'Burning Duration', 'Non-Candle Product Type', 'Usage Instructions', 'Safety Warnings'
        ] : hasTablewareDiningCategory ? [
          'Product Description', 'Style Feature 1', 'Style Feature 2', 'Style Feature 3', 'Style Feature 4', 'Style Feature 5', 'Style Feature 6',
          'Material Composition', 'Care Instructions', 'Additional Care Notes',
          'Color/Finish Options', 'Origin', 'Capacity', 'Set Contents', 'Safety Warnings', 'Food Safe Certification'
        ] : [
          'Product Description', 'Style Feature 1', 'Style Feature 2', 'Style Feature 3', 'Style Feature 4', 'Style Feature 5', 'Style Feature 6',
          'Material Composition', 'Care Instructions', 'Additional Care Notes',
          'Fit Type', 'Model Wearing Size', 'Model Age Range', 'Model measurements (CM)', 'Additional Fit Notes'
        ]
      ) : [];

      const headers = [...baseHeaders, ...productDetailsHeaders];
      const formHeaders = ['Brand Name', 'Tax Inclusive'];
      const allHeaders = formHeaders.concat(headers);

      const rows = products.map(product => {
        const formValues = [formData.brandName, formData.taxInclusive];
        const baseProductData = isShopify 
          ? [product.productLinks, product.category, product.subcategory, product.sku, product.inventoryAvailability, product.leadTime, product.price, product.quantity, product.weight, product.length, product.width, product.height, product.packagingType]
          : [product.productName, product.description, product.category, product.subcategory, product.sku, product.inventoryAvailability, product.leadTime, product.price, product.quantity, product.size, product.color, product.pattern, product.weight, product.length, product.width, product.height, product.mainImage, product.additionalImages, product.notes, product.packagingType];
        
        // Add conditional product details data
        const productDetailsData = products.some(p => shouldShowProductDetailsFields(p.category)) ? (
          shouldShowBagFields(product.category) ? [
            product.productDescription || '',
            product.styleFeature1 || '',
            product.styleFeature2 || '',
            product.styleFeature3 || '',
            product.styleFeature4 || '',
            product.styleFeature5 || '',
            product.styleFeature6 || '',
            product.materialComposition || '',
            product.liningMaterial || '',
            product.hardwareFinish || '',
            product.careInstructions || '',
            product.additionalCareNotes || '',
            product.handleType || '',
            product.handleStrapDrop || '',
            product.strapAdjustable || ''
          ] : shouldShowHomeDecorFields(product.category) ? [
            product.productDescription || '',
            product.styleFeature1 || '',
            product.styleFeature2 || '',
            product.styleFeature3 || '',
            product.styleFeature4 || '',
            product.styleFeature5 || '',
            product.styleFeature6 || '',
            product.materialComposition || '',
            product.careInstructions || '',
            product.additionalCareNotes || '',
            product.colorFinishOptions || '',
            product.origin || '',
            product.setContents || '',
            product.additionalInformation || '',
            product.limitedEditionInfo || '',
            product.frameMaterial || '',
            product.photoArtSizeCapacity || '',
            product.orientation || '',
            product.mountingType || ''
          ] : shouldShowCandlesFragrancesFields(product.category) ? [
            product.productDescription || '',
            product.styleFeature1 || '',
            product.styleFeature2 || '',
            product.styleFeature3 || '',
            product.styleFeature4 || '',
            product.styleFeature5 || '',
            product.styleFeature6 || '',
            product.materialComposition || '',
            product.careInstructions || '',
            product.additionalCareNotes || '',
            product.scentFragrance || '',
            product.colorFinishOptions || '',
            product.origin || '',
            product.capacity || '',
            product.setContents || '',
            product.burningDuration || '',
            product.nonCandleProductType || '',
            product.usageInstructions || '',
            product.safetyWarnings || ''
          ] : shouldShowTablewareDiningFields(product.category) ? [
            product.productDescription || '',
            product.styleFeature1 || '',
            product.styleFeature2 || '',
            product.styleFeature3 || '',
            product.styleFeature4 || '',
            product.styleFeature5 || '',
            product.styleFeature6 || '',
            product.materialComposition || '',
            product.careInstructions || '',
            product.additionalCareNotes || '',
            product.colorFinishOptions || '',
            product.origin || '',
            product.tablewareCapacity || '',
            product.setContents || '',
            product.safetyWarnings || '',
            product.foodSafeCertification || ''
          ] : [
            product.productDescription || '',
            product.styleFeature1 || '',
            product.styleFeature2 || '',
            product.styleFeature3 || '',
            product.styleFeature4 || '',
            product.styleFeature5 || '',
            product.styleFeature6 || '',
            product.materialComposition || '',
            product.careInstructions || '',
            product.additionalCareNotes || '',
            product.fitType || '',
            product.modelWearingSize || '',
            product.modelHeight || '',
            product.additionalFitNotes || ''
          ]
        ) : [];

        const productData = [...baseProductData, ...productDetailsData];
        return [...formValues, ...productData];
      });

      const payload = {
        headers: allHeaders,
        data: rows,
        brandName: formData.brandName.trim(),
        files: {}, // Empty files object (no file uploads for Product Onboarding)
        fileId: currentFileId,
        updateExisting: updateExisting && !!currentFileId
      };

      // Use no-cors mode for POST (Google Apps Script Web Apps don't support CORS with custom headers)
      // Data will save successfully, but we can't read the response
      console.log('📤 Sending POST request to:', scriptUrl);
      console.log('📦 Payload:', { 
        brandName: payload.brandName, 
        headersCount: payload.headers.length,
        dataRowsCount: payload.data.length,
        hasFileId: !!payload.fileId,
        updateExisting: payload.updateExisting
      });
      
      await fetch(scriptUrl, {
        method: 'POST',
        mode: 'no-cors',
        body: JSON.stringify(payload)
      });
      
      console.log('✅ POST request sent (response not readable due to no-cors)');
      
      // If updating existing file, we already have fileId
      let newFileId = currentFileId;
      if (updateExisting && currentFileId) {
        console.log('📝 Updating existing file, fileId:', currentFileId);
        newFileId = currentFileId;
      } else {
        // For new files, try to get fileId by making a GET request after a short delay
        // This is a workaround since we can't read POST response with no-cors
        console.log('🆕 New file created, will attempt to retrieve fileId via GET request...');
        console.log('⏳ Waiting 3 seconds for file to be created in Google Drive...');
        setIsGeneratingLink(true); // Show spinner while generating link
        
        // Wait for file to be created, then try to get it using iframe + postMessage (bypasses CORS)
        setTimeout(() => {
          const tryGetFileId = (attempt = 1, maxAttempts = 5) => {
            console.log(`🔍 Attempt ${attempt}/${maxAttempts} to retrieve fileId for brand: "${formData.brandName.trim()}"`);
            
            // Create a hidden iframe to load the Google Apps Script
            const iframe = document.createElement('iframe');
            iframe.style.display = 'none';
            iframe.style.width = '0';
            iframe.style.height = '0';
            iframe.style.border = 'none';
            
            // Set up message listener
            const messageHandler = (event) => {
              console.log('📨 Message received:', event.origin, event.data);
              
              // Verify origin for security (adjust if needed)
              if (event.data && event.data.type === 'fileIdResponse') {
                console.log('✅ Received fileIdResponse:', event.data);
                
                // Clean up
                window.removeEventListener('message', messageHandler);
                if (iframe.parentNode) {
                  iframe.remove();
                }
                
                if (event.data.success && event.data.fileId) {
                  const retrievedFileId = event.data.fileId;
                  setCurrentFileId(retrievedFileId);
                  setFileIdVerified(true); // Mark fileId as verified from Google - checkbox will appear
                  setIsGeneratingLink(false); // Hide spinner when link is generated
                  const baseUrl = window.location.origin;
                  const platformPath = platform || (isShopify ? 'shopify' : 'non-shopify');
                  const retrievedShareLink = `${baseUrl}/${platformPath}/product-onboarding?fileId=${retrievedFileId}`;
                  setShareableLink(retrievedShareLink);
                  setSearchParams({ fileId: retrievedFileId });
                  setUpdateExisting(true);
                  // Don't auto-check checkbox - let user decide when to show share section
                  console.log('✅ Retrieved fileId via postMessage:', retrievedFileId);
                  console.log('✅ Shareable link:', retrievedShareLink);
                  
                  toast({
                    title: 'Shareable link generated!',
                    description: 'File ID retrieved successfully',
                    status: 'success',
                    duration: 3000,
                    isClosable: true,
                  });
                } else {
                  console.log('❌ fileIdResponse received but no fileId:', event.data);
                  if (attempt < maxAttempts) {
                    console.log(`⏳ Retrying in ${2000 * attempt}ms...`);
                    setTimeout(() => tryGetFileId(attempt + 1, maxAttempts), 2000 * attempt);
                  } else {
                    setIsGeneratingLink(false); // Hide spinner when all attempts fail
                    console.log('❌ All attempts failed. Error:', event.data.error || event.data.message);
                  }
                }
              } else {
                console.log('⚠️ Received message but not fileIdResponse:', event.data);
              }
            };
            
            window.addEventListener('message', messageHandler);
            
            // Use JSONP callback approach instead of postMessage HTML (more reliable)
            const callbackName = `processFileId_${Date.now()}_${attempt}`;
            
            // Set up the callback function on window
            window[callbackName] = (data) => {
              console.log('📨 JSONP callback received:', data);
              
              // Clean up
              delete window[callbackName];
              const script = document.getElementById(`jsonp-script-${callbackName}`);
              if (script) script.remove();
              window.removeEventListener('message', messageHandler);
              if (iframe.parentNode) iframe.remove();
              
              if (data && data.success && data.fileId) {
                const retrievedFileId = data.fileId;
                setCurrentFileId(retrievedFileId);
                setFileIdVerified(true); // Mark fileId as verified from Google - checkbox will appear
                setIsGeneratingLink(false); // Hide spinner when link is generated
                const baseUrl = window.location.origin;
                const platformPath = platform || (isShopify ? 'shopify' : 'non-shopify');
                const retrievedShareLink = `${baseUrl}/${platformPath}/product-onboarding?fileId=${retrievedFileId}`;
                setShareableLink(retrievedShareLink);
                setSearchParams({ fileId: retrievedFileId });
                setUpdateExisting(true);
                // Don't auto-check checkbox - let user decide when to show share section
                console.log('✅ Retrieved fileId via JSONP:', retrievedFileId);
                
                toast({
                  title: 'Shareable link generated!',
                  description: 'File ID retrieved successfully',
                  status: 'success',
                  duration: 3000,
                  isClosable: true,
                });
              } else {
                console.log('❌ JSONP callback received but no fileId:', data);
                if (attempt < maxAttempts) {
                  setTimeout(() => tryGetFileId(attempt + 1, maxAttempts), 2000 * attempt);
                } else {
                  setIsGeneratingLink(false); // Hide spinner when all attempts fail
                }
              }
            };
            
            // Create script tag for JSONP (more reliable than iframe)
            const script = document.createElement('script');
            script.id = `jsonp-script-${callbackName}`;
            let getUrl = `${scriptUrl}?action=getLatest&brandName=${encodeURIComponent(formData.brandName.trim())}&callback=${callbackName}`;
            
            // Remove any /u/7/ or /u/X/ patterns that might have been added
            getUrl = getUrl.replace(/\/u\/\d+\//g, '/');
            
            script.src = getUrl;
            script.onerror = () => {
              console.error(`❌ JSONP script error (attempt ${attempt})`);
              delete window[callbackName];
              script.remove();
              window.removeEventListener('message', messageHandler);
              if (iframe.parentNode) iframe.remove();
              if (attempt < maxAttempts) {
                setTimeout(() => tryGetFileId(attempt + 1, maxAttempts), 2000 * attempt);
              }
            };
            
            console.log(`🌐 Loading JSONP script with URL:`, getUrl);
            console.log(`🔍 Script URL base:`, scriptUrl);
            
            // Use script tag for JSONP (bypasses CORS and iframe issues)
            document.head.appendChild(script);
            
            // Timeout after 10 seconds
            setTimeout(() => {
              if (script.parentNode) {
                console.log(`⏱️ JSONP attempt ${attempt} timed out after 10 seconds`);
                delete window[callbackName];
                script.remove();
                window.removeEventListener('message', messageHandler);
                if (attempt < maxAttempts) {
                  console.log(`⏳ Retrying in ${2000 * attempt}ms...`);
                  setTimeout(() => tryGetFileId(attempt + 1, maxAttempts), 2000 * attempt);
                } else {
                  console.log('❌ All JSONP attempts failed.');
                  toast({
                    title: 'Unable to generate shareable link',
                    description: 'Please check that the Google Apps Script is deployed correctly',
                    status: 'warning',
                    duration: 5000,
                    isClosable: true,
                  });
                }
              }
            }, 10000);
          };
          
          tryGetFileId(1, 5);
        }, 3000);
      }

      // Clear localStorage after successful save
      localStorage.removeItem('brandOnboardingData');
      
      // Generate shareable link if we have a fileId (for updating existing files)
      const baseUrl = window.location.origin;
      const platformPath = platform || (isShopify ? 'shopify' : 'non-shopify');
      let generatedShareLink = null;
      
      // Generate share link if we have fileId (for updating existing files)
      if (newFileId) {
        generatedShareLink = `${baseUrl}/${platformPath}/product-onboarding?fileId=${newFileId}`;
        setShareableLink(generatedShareLink);
        setSearchParams({ fileId: newFileId });
        setUpdateExisting(true);
        console.log('✅ Generated shareable link:', generatedShareLink);
      } else {
        // For new files, fileId will be retrieved via GET request (see setTimeout above)
        console.log('⏳ Waiting for GET request to retrieve fileId...');
      }
      
      toast({
        title: updateExisting && currentFileId ? 'Data updated' : 'Data saved',
        description: generatedShareLink ? 'Shareable link generated!' : 'Data saved successfully. Retrieving shareable link...',
        status: 'success',
        duration: 3000,
        isClosable: true,
      });

    } catch (error) {
      toast({
        title: 'Error saving data',
        description: 'Please check your internet connection and try again.',
        status: 'error',
        duration: 4000,
        isClosable: true,
      });
    } finally {
      setIsSaving(false);
    }
  };

  const importExcel = useCallback((event) => {
    const file = event.target.files[0];
    if (!file) return;

    const reader = new FileReader();
    reader.onload = (e) => {
      try {
        const workbook = XLSX.read(e.target.result, { type: 'binary' });
        const firstSheet = workbook.Sheets[workbook.SheetNames[0]];
        const data = XLSX.utils.sheet_to_json(firstSheet, { header: 1 });

        if (data.length < 2) {
          toast({
            title: 'Import Error',
            description: 'Excel file is empty or invalid.',
            status: 'error',
            duration: 3000,
            isClosable: true,
          });
          return;
        }

        const headers = data[0];
        
        // Helper function to find header index case-insensitively
        const findHeaderIndex = (headerName) => {
          return headers.findIndex(h => h && h.toString().toLowerCase().trim() === headerName.toLowerCase().trim());
        };
        
        // Packaging options for case-insensitive matching
        const packagingOptions = ['Box', 'Envelope', 'Poly Mailer', 'Bubble Mailer', 'Padded Envelope', 'Other'];
        
        // Pattern options for case-insensitive matching
        const patternOptions = ['Floral', 'Geometric', 'Stripes', 'Abstract', 'Traditional Motifs', 'Animal Print'];
        
        // Get all unique color options from all categories for case-insensitive matching
        const getAllColorOptions = () => {
          const allColors = new Set();
          // Get colors for each category type
          Object.keys(categories).forEach(category => {
            const colors = getColorOptions(category);
            colors.forEach(color => allColors.add(color));
          });
          return Array.from(allColors);
        };
        const allColorOptions = getAllColorOptions();
        
        // Get all unique size options from all categories for case-insensitive matching
        const getAllSizeOptions = () => {
          const allSizes = new Set();
          // Get sizes for each category and subcategory combination
          Object.keys(categories).forEach(category => {
            // Get sizes for category without subcategory
            const sizes = getSizeOptions(category);
            sizes.forEach(size => allSizes.add(size));
            // Also get sizes for each subcategory
            if (categories[category]) {
              categories[category].forEach(subcategory => {
                const subSizes = getSizeOptions(category, subcategory);
                subSizes.forEach(size => allSizes.add(size));
              });
            }
          });
          return Array.from(allSizes);
        };
        const allSizeOptions = getAllSizeOptions();
        
        // Helper function to normalize strings for matching (remove extra spaces, normalize punctuation)
        const normalizeString = (str) => {
          if (!str) return '';
          return str.toString()
            .toLowerCase()
            .trim()
            .replace(/-/g, ' ') // Replace hyphens with spaces (so "T-shirt" matches "T shirt")
            .replace(/\s+/g, ' ') // Replace multiple spaces with single space
            .replace(/\s*\(\s*/g, ' (') // Normalize spaces around opening parenthesis
            .replace(/\s*\)\s*/g, ') ') // Normalize spaces around closing parenthesis
            .replace(/\s*&\s*/g, ' & ') // Normalize spaces around ampersand (ensure space before and after)
            .replace(/\s*\/\s*/g, ' / ') // Normalize spaces around forward slash
            .trim();
        };
        
        // Helper function to match value case-insensitively against options
        const matchCaseInsensitive = (value, options) => {
          if (!value || !options || options.length === 0) return value || '';
          const normalizedValue = normalizeString(value);
          
          // First try exact normalized match
          let matched = options.find(opt => normalizeString(opt) === normalizedValue);
          if (matched) return matched;
          
          // If no exact match, try matching with plural/singular variations
          // Check if one ends with the other + 's' or vice versa (for entire string)
          matched = options.find(opt => {
            const normalizedOpt = normalizeString(opt);
            
            // Exact match already tried above, so skip
            if (normalizedOpt === normalizedValue) return false;
            
            // Check if one is the other with 's' added/removed at the end (entire string)
            // Handle cases like "sports bra" vs "sports bras"
            if (normalizedValue.endsWith('s') && normalizedValue.slice(0, -1) === normalizedOpt) {
              return true; // "bras" matches "bra"
            }
            if (normalizedOpt.endsWith('s') && normalizedOpt.slice(0, -1) === normalizedValue) {
              return true; // "bra" matches "bras"
            }
            
            // Check if they match when ignoring trailing 's' on words
            // Split by spaces and compare word by word, allowing 's' difference on any word
            const valueWords = normalizedValue.split(/\s+/);
            const optWords = normalizedOpt.split(/\s+/);
            
            if (valueWords.length === optWords.length) {
              let allMatch = true;
              for (let i = 0; i < valueWords.length; i++) {
                const valueWord = valueWords[i];
                const optWord = optWords[i];
                
                // Skip special characters/symbols (like &, /, etc.) - they should match exactly
                if (/^[&/()]+$/.test(valueWord) || /^[&/()]+$/.test(optWord)) {
                  if (valueWord !== optWord) {
                    allMatch = false;
                    break;
                  }
                  continue;
                }
                
                // Check if words match with or without trailing 's'
                if (valueWord === optWord ||
                    (valueWord.endsWith('s') && valueWord.slice(0, -1) === optWord) ||
                    (optWord.endsWith('s') && optWord.slice(0, -1) === valueWord)) {
                  continue; // Words match
                } else {
                  allMatch = false;
                  break;
                }
              }
              if (allMatch) {
                return true;
              }
            }
            
            return false;
          });
          
          return matched || value; // Return matched option or original value if no match
        };
        
        // Set form values
        const formFields = {
          'Brand Name': 'brandName',
          'Tax Inclusive': 'taxInclusive',
        };

        const newFormData = { ...formData };
        Object.keys(formFields).forEach(fieldName => {
          const fieldIndex = findHeaderIndex(fieldName);
          if (fieldIndex >= 0 && data.length > 1 && data[1][fieldIndex]) {
            const value = data[1][fieldIndex];
            newFormData[formFields[fieldName]] = value ? value.toString().trim() : '';
          }
        });
        setFormData(newFormData);

        // Set product data
        const newProducts = [];
        for (let i = 1; i < data.length; i++) {
          const rowData = data[i];
          if (rowData.every(cell => !cell || cell.toString().trim() === '')) {
            continue;
          }

          const product = {
            id: i,
            productName: '',
            productLinks: '',
            description: '',
            category: '',
            subcategory: '',
            sku: '',
            inventoryAvailability: '',
            leadTime: '',
            price: '',
            quantity: '',
            size: '',
            color: '',
            pattern: '',
            weight: '',
            length: '',
            width: '',
            height: '',
            mainImage: '',
            additionalImages: '',
            tags: '',
            notes: '',
            packagingType: '',
            // Product Details Section
            productDescription: '',
            styleFeature1: '',
            styleFeature2: '',
            styleFeature3: '',
            styleFeature4: '',
            styleFeature5: '',
            styleFeature6: '',
            materialComposition: '',
            careInstructions: '',
            additionalCareNotes: '',
            // Fit Section
            fitType: '',
            modelWearingSize: '',
            modelHeight: '',
            additionalFitNotes: '',
            // Bag-specific fields
            liningMaterial: '',
            hardwareFinish: '',
            handleType: '',
            handleStrapDrop: '',
            strapAdjustable: '',
            // Home Decor fields
            colorFinishOptions: '',
            origin: '',
            setContents: '',
            additionalInformation: '',
            limitedEditionInfo: '',
            frameMaterial: '',
            photoArtSizeCapacity: '',
            orientation: '',
            mountingType: '',
            // Candles & Fragrances fields
            scentFragrance: '',
            capacity: '',
            burningDuration: '',
            nonCandleProductType: '',
            usageInstructions: '',
            safetyWarnings: '',
            // Tableware / Dining fields
            tablewareCapacity: '',
            foodSafeCertification: ''
          };

          // Get header indices case-insensitively
          const categoryIndex = findHeaderIndex('Category');
          const subcategoryIndex = findHeaderIndex('Subcategory');
          const packagingIndex = findHeaderIndex('Packaging Type');

          // Skip form fields, map remaining data
          let dataIndex = 0;
          headers.forEach(header => {
            if (!header || Object.keys(formFields).includes(header)) {
              dataIndex++;
              return;
            }
            
            const headerLower = header.toString().toLowerCase().trim();
            const rawValue = rowData[dataIndex];
            const value = rawValue ? rawValue.toString().trim() : '';
            
            // Handle Category with case-insensitive matching
            if (headerLower === 'category') {
              const categoryOptions = Object.keys(categories);
              const matchedCategory = matchCaseInsensitive(value, categoryOptions);
              // Verify that a match was actually found (matched value should be in the categoryOptions array)
              if (matchedCategory && categoryOptions.includes(matchedCategory)) {
                product.category = matchedCategory;
              } else {
                product.category = value; // Keep original value if no match found
              }
            }
            // Handle Subcategory with case-insensitive matching
            else if (headerLower === 'subcategory') {
              let matchedSubcategory = null;
              let matchedCategory = null;
              
              // First try to match against subcategories of the matched category (if category is already set)
              if (product.category && categories[product.category]) {
                const subcats = categories[product.category];
                const matched = matchCaseInsensitive(value, subcats);
                // Verify that a match was actually found (matched value should be in the subcats array)
                if (matched && subcats.includes(matched)) {
                  matchedSubcategory = matched;
                  matchedCategory = product.category;
                }
              }
              
              // If no match found yet, try all subcategories across all categories
              if (!matchedSubcategory) {
                for (const [cat, subcats] of Object.entries(categories)) {
                  const matched = matchCaseInsensitive(value, subcats);
                  // Check if a match was found by verifying the matched value is in the options array
                  if (matched && subcats.includes(matched)) {
                    matchedSubcategory = matched;
                    matchedCategory = cat;
                    break; // Use first match found
                  }
                }
              }
              
              // Set the subcategory (matched value or original if no match)
              if (matchedSubcategory) {
                product.subcategory = matchedSubcategory;
                // Also set category if not set
                if (!product.category && matchedCategory) {
                  product.category = matchedCategory;
                }
              } else {
                product.subcategory = value; // Keep original value if no match found
              }
            }
            // Handle Packaging Type with case-insensitive matching
            else if (headerLower === 'packaging type') {
              const matchedPackaging = matchCaseInsensitive(value, packagingOptions);
              // Verify that a match was actually found
              if (matchedPackaging && packagingOptions.includes(matchedPackaging)) {
                product.packagingType = matchedPackaging;
              } else {
                product.packagingType = value; // Keep original value if no match found
              }
            }
            // Handle Color with case-insensitive matching
            else if (headerLower === 'color') {
              // Try to match against all possible colors
              const matchedColor = matchCaseInsensitive(value, allColorOptions);
              // Verify that a match was actually found
              if (matchedColor && allColorOptions.includes(matchedColor)) {
                product.color = matchedColor;
              } else {
                product.color = value; // Keep original value if no match found
              }
            }
            // Handle Pattern/Print with case-insensitive matching
            else if (headerLower === 'pattern/print' || headerLower === 'pattern') {
              const matchedPattern = matchCaseInsensitive(value, patternOptions);
              // Verify that a match was actually found
              if (matchedPattern && patternOptions.includes(matchedPattern)) {
                product.pattern = matchedPattern;
              } else {
                product.pattern = value; // Keep original value if no match found
              }
            }
            // Handle Size with case-insensitive matching
            else if (headerLower === 'size') {
              let matchedSize = null;
              let sizeOptions = [];
              
              // First try to match against category-specific size options if category/subcategory are set
              if (product.category) {
                sizeOptions = getSizeOptions(product.category, product.subcategory);
                matchedSize = matchCaseInsensitive(value, sizeOptions);
                // Verify that a match was actually found
                if (matchedSize && sizeOptions.includes(matchedSize)) {
                  product.size = matchedSize;
                }
              }
              
              // If no match found yet, try matching against all possible size options
              if (!matchedSize || !sizeOptions.includes(matchedSize)) {
                matchedSize = matchCaseInsensitive(value, allSizeOptions);
                // Verify that a match was actually found
                if (matchedSize && allSizeOptions.includes(matchedSize)) {
                  product.size = matchedSize;
                } else {
                  product.size = value; // Keep original value if no match found
                }
              }
            }
            // Handle other fields normally
            else {
              switch (header) {
                case 'Product Name': product.productName = value; break;
                case 'Product Links': product.productLinks = value; break;
                case 'Description': product.description = value; break;
                case 'SKU/Product ID': product.sku = value; break;
                case 'Inventory Availability': product.inventoryAvailability = value; break;
                case 'Lead Time (Days)': product.leadTime = value; break;
                case 'Price (Local Currency)':
                case 'Final Prices': product.price = value; break;
                case 'Quantity in Stock': product.quantity = value; break;
                case 'Actual Weight (kg/g)':
                case 'Weight (kg/g)': product.weight = value; break;
                case 'Length (cm)': product.length = value; break;
                case 'Width (cm)': product.width = value; break;
                case 'Height (cm)': product.height = value; break;
                case 'Main Image File Name': product.mainImage = value; break;
                case 'Additional Image File Names': product.additionalImages = value; break;
                case 'Notes': product.notes = value; break;
                // Product Details Section
                case 'Product Description': product.productDescription = value; break;
                case 'Style Feature 1': product.styleFeature1 = value; break;
                case 'Style Feature 2': product.styleFeature2 = value; break;
                case 'Style Feature 3': product.styleFeature3 = value; break;
                case 'Style Feature 4': product.styleFeature4 = value; break;
                case 'Style Feature 5': product.styleFeature5 = value; break;
                case 'Style Feature 6': product.styleFeature6 = value; break;
                case 'Material Composition': product.materialComposition = value; break;
                case 'Lining Material': product.liningMaterial = value; break;
                case 'Hardware Finish': product.hardwareFinish = value; break;
                case 'Care Instructions': product.careInstructions = value; break;
                case 'Additional Care Notes': product.additionalCareNotes = value; break;
                // Bag-specific fields
                case 'Handle Type': product.handleType = value; break;
                case 'Handle/Strap Drop': product.handleStrapDrop = value; break;
                case 'Strap - Adjustable?': product.strapAdjustable = value; break;
                // Home Decor fields
                case 'Color/Finish Options': product.colorFinishOptions = value; break;
                case 'Origin': product.origin = value; break;
                case 'Set Contents': product.setContents = value; break;
                case 'Additional Information': product.additionalInformation = value; break;
                case 'Limited Edition Info': product.limitedEditionInfo = value; break;
                case 'Frame Material': product.frameMaterial = value; break;
                case 'Photo/Art Size Capacity': product.photoArtSizeCapacity = value; break;
                case 'Orientation': product.orientation = value; break;
                case 'Mounting Type': product.mountingType = value; break;
                // Candles & Fragrances fields
                case 'Scent/Fragrance': product.scentFragrance = value; break;
                case 'Capacity': product.capacity = value; break;
                case 'Burning Duration': product.burningDuration = value; break;
                case 'Non-Candle Product Type': product.nonCandleProductType = value; break;
                case 'Usage Instructions': product.usageInstructions = value; break;
                case 'Safety Warnings': product.safetyWarnings = value; break;
                // Tableware / Dining fields
                case 'Food Safe Certification': product.foodSafeCertification = value; break;
                // Fit Section
                case 'Fit Type': product.fitType = value; break;
                case 'Model Wearing Size': product.modelWearingSize = value; break;
                case 'Model Age Range': product.modelAgeRange = value; break;
                case 'Model measurements (CM)':
                case 'Model Height (cm)': product.modelHeight = value; break;
                case 'Additional Fit Notes': product.additionalFitNotes = value; break;
              }
            }
            dataIndex++;
          });

          newProducts.push(product);
        }

        if (newProducts.length > 0) {
          setProducts(newProducts);
        }

        toast({
          title: 'Import Successful',
          description: 'Excel file imported successfully!',
          status: 'success',
          duration: 3000,
          isClosable: true,
        });
      } catch (error) {
        toast({
          title: 'Import Error',
          description: 'Failed to import Excel file.',
          status: 'error',
          duration: 3000,
          isClosable: true,
        });
      }
    };
    reader.readAsBinaryString(file);
    event.target.value = '';
  }, [formData, toast]);

  return (
    <ChakraProvider theme={theme}>
      <Box minH="100vh" bg="gray.50">
        <Container maxW="container.xl" py={{ base: 4, md: 8 }} px={{ base: 4, md: 6 }}>
          {/* Logo */}
          <Box mb={6} textAlign="center">
            <Image 
              src="/logo.svg" 
              alt="Pose Logo" 
              maxH="80px"
              maxW="200px"
              mx="auto"
            />
          </Box>

          <Card p={{ base: 2, md: 0 }}>
            <CardHeader p={{ base: 3, md: 6 }}>
              <VStack spacing={4}>
                <Heading size={{ base: "md", md: "lg" }} color="black" textAlign={{ base: "center", md: "left" }}>
                  Brand Onboarding Checklist
                </Heading>
                <Text color="black" fontSize={{ base: "sm", md: "md" }} textAlign={{ base: "center", md: "left" }}>
                  Complete product information form - Fill all fields and download when complete
                </Text>
                
                {!hidePlatformToggle && (
                  <Box 
                    bg="yellow.50" 
                    border="2px solid" 
                    borderColor="yellow.400" 
                    borderRadius="md" 
                    p={4} 
                    w="full"
                    mb={2}
                  >
                    <HStack spacing={4} align="center" wrap="wrap" justifyContent={{ base: "center", md: "flex-start" }}>
                      <Text fontWeight="bold" fontSize={{ base: "sm", md: "md" }} color="yellow.800">Platform:</Text>
                      <Text fontSize={{ base: "sm", md: "md" }} fontWeight="medium">Non-Shopify</Text>
                      <Switch 
                        colorScheme="yellow" 
                        isChecked={isShopify} 
                        onChange={(e) => setIsShopify(e.target.checked)}
                        size={{ base: "sm", md: "md" }}
                      />
                      <Text fontSize={{ base: "sm", md: "md" }} fontWeight="medium">Shopify</Text>
                      <Badge colorScheme={isShopify ? "green" : "blue"} variant="solid" fontSize={{ base: "xs", md: "sm" }} px={2} py={1}>
                        {isShopify ? "Shopify Mode" : "Non-Shopify Mode"}
                      </Badge>
                    </HStack>
                  </Box>
                )}
                
                {hidePlatformToggle && (
                  <Box 
                    bg={platform === 'shopify' ? "green.50" : "blue.50"} 
                    border="2px solid" 
                    borderColor={platform === 'shopify' ? "green.400" : "blue.400"} 
                    borderRadius="md" 
                    p={4} 
                    w="full"
                    mb={2}
                  >
                    <HStack spacing={4} align="center" wrap="wrap" justifyContent={{ base: "center", md: "flex-start" }}>
                      <Text fontWeight="bold" fontSize={{ base: "sm", md: "md" }} color={platform === 'shopify' ? "green.800" : "blue.800"}>
                        Platform: 
                      </Text>
                      <Badge colorScheme={platform === 'shopify' ? "green" : "blue"} variant="solid" fontSize={{ base: "xs", md: "sm" }} px={2} py={1}>
                        {platform === 'shopify' ? "Shopify Mode" : "Non-Shopify Mode"}
                      </Badge>
                    </HStack>
                  </Box>
                )}
              </VStack>
            </CardHeader>

            <CardBody p={{ base: 3, md: 6 }}>
              {/* Form Section */}
              <Card variant="outline" mb={4} borderWidth="1px" borderColor="gray.200">
                <CardHeader bg="gray.50" borderBottom="1px solid" borderColor="gray.200" py={2} px={{ base: 3, md: 4 }}>
                  <Heading size={{ base: "xs", md: "sm" }} color="black">Common Information</Heading>
                </CardHeader>
                <CardBody p={{ base: 3, md: 4 }}>
                  <HStack spacing={3} wrap="wrap" w="full" direction={{ base: "column", md: "row" }} align={{ base: "stretch", md: "flex-start" }}>
                    <FormControl maxW={{ base: "100%", md: "250px" }} isInvalid={!!errors.brandName}>
                      <FormLabel fontSize={{ base: "xs", md: "sm" }} fontWeight="semibold" color="gray.700" mb={1}>
                        Brand Name <Text as="span" color="red.500">*</Text>
                      </FormLabel>
                      <Input
                        size="sm"
                        value={formData.brandName}
                        onChange={(e) => handleFormChange('brandName', e.target.value)}
                        placeholder="Enter brand name"
                        bg="white"
                        borderColor={errors.brandName ? "red.500" : "gray.300"}
                      />
                      {errors.brandName && (
                        <FormErrorMessage fontSize="xs">{errors.brandName}</FormErrorMessage>
                      )}
                    </FormControl>

                    <FormControl maxW={{ base: "100%", md: "200px" }} isInvalid={!!errors.taxInclusive}>
                      <FormLabel fontSize={{ base: "xs", md: "sm" }} fontWeight="semibold" color="gray.700" mb={1}>
                        Tax Inclusive <Text as="span" color="red.500">*</Text>
                      </FormLabel>
                      <Select
                        size="sm"
                        value={formData.taxInclusive}
                        onChange={(e) => handleFormChange('taxInclusive', e.target.value)}
                        placeholder="Select"
                        bg="white"
                        borderColor={errors.taxInclusive ? "red.500" : "gray.300"}
                        _hover={{ borderColor: errors.taxInclusive ? "red.500" : "gray.400" }}
                        _focus={{ borderColor: errors.taxInclusive ? "red.500" : "blue.500", boxShadow: errors.taxInclusive ? "0 0 0 1px red.500" : "0 0 0 1px blue.500" }}
                      >
                        <option value="Yes">Yes</option>
                        <option value="No">No</option>
                      </Select>
                      <Text fontSize="xs" color="red.500" mt={1}>
                        Does final price include VAT or not?
                      </Text>
                      {errors.taxInclusive && (
                        <FormErrorMessage fontSize="xs">{errors.taxInclusive}</FormErrorMessage>
                      )}
                    </FormControl>

                  </HStack>
                </CardBody>
              </Card>

              {/* Action Buttons */}
              <HStack spacing={3} mb={4} wrap="wrap" justifyContent="space-between" w="full">
                <HStack spacing={3} wrap="wrap" justifyContent={{ base: "center", md: "flex-start" }}>
                  <Button
                    leftIcon={<AddIcon />}
                    colorScheme="green"
                    size={{ base: "xs", md: "sm" }}
                    onClick={addProduct}
                    w={{ base: "100%", sm: "auto" }}
                  >
                    Add Product Row
                  </Button>

                  <Button
                    leftIcon={<AttachmentIcon />}
                    colorScheme="purple"
                    size={{ base: "xs", md: "sm" }}
                    onClick={() => document.getElementById('fileInput').click()}
                    w={{ base: "100%", sm: "auto" }}
                  >
                    Import Excel
                  </Button>
                  <input
                    id="fileInput"
                    type="file"
                    accept=".xlsx,.xls"
                    onChange={importExcel}
                    style={{ display: 'none' }}
                  />

                  <Button
                    leftIcon={<DownloadIcon />}
                    bg="black"
                    color="white"
                    size={{ base: "xs", md: "sm" }}
                    onClick={downloadExcel}
                    _hover={{ bg: "gray.800" }}
                    w={{ base: "100%", sm: "auto" }}
                  >
                    Download Excel
                  </Button>
                </HStack>

                <HStack spacing={3} align="center">
                  {/* Show spinner when generating link */}
                  {isGeneratingLink && (
                    <HStack spacing={2}>
                      <Spinner size="sm" color="green.500" />
                      <Text fontSize={{ base: "xs", md: "sm" }} color="gray.600">
                        Generating shareable link...
                      </Text>
                    </HStack>
                  )}
                  
                  {/* Show checkbox only when fileId is verified from Google */}
                  {!isGeneratingLink && fileIdVerified && (shareableLink || currentFileId) && (
                    <Checkbox
                      isChecked={showShareSection}
                      onChange={(e) => setShowShareSection(e.target.checked)}
                      colorScheme="green"
                      size={{ base: "sm", md: "md" }}
                    >
                      <Text fontSize={{ base: "xs", md: "sm" }} color="black">
                        Show shareable link
                      </Text>
                    </Checkbox>
                  )}
                  
                  <Button
                    leftIcon={<FaUndo />}
                    bg="red.500"
                    color="white"
                    size={{ base: "xs", md: "sm" }}
                    onClick={resetForm}
                    w={{ base: "100%", md: "auto" }}
                    isDisabled={isSaving || isLoadingData || isGeneratingLink}
                    _hover={{ bg: "red.600" }}
                  >
                    Reset
                  </Button>
                  
                  <Button
                    leftIcon={<FaSave />}
                    bg="black"
                    color="white"
                    size={{ base: "xs", md: "sm" }}
                    onClick={saveToGoogleSheets}
                    w={{ base: "100%", md: "auto" }}
                    isLoading={isSaving || isLoadingData}
                    loadingText={isLoadingData ? "Loading..." : "Saving..."}
                    isDisabled={isSaving || isLoadingData || isGeneratingLink}
                    _hover={{ bg: "gray.800" }}
                  >
                    {updateExisting && currentFileId ? 'Update' : 'Save'}
                  </Button>
                </HStack>
              </HStack>

              {/* Shareable Link Section */}
              {showShareSection && (shareableLink || currentFileId) && (
                <Card variant="outline" mb={4} borderWidth="2px" borderColor="green.300" bg="green.50">
                  <CardHeader pb={2}>
                    <Heading size="sm" color="green.700">Shareable Link Generated!</Heading>
                  </CardHeader>
                  <CardBody pt={0}>
                    <VStack spacing={3} align="stretch">
                      <Box p={3} bg="white" borderRadius="md" border="1px solid" borderColor="green.200">
                        <Text fontSize="xs" color="gray.600" mb={1} fontWeight="semibold">Share this link with the merchant:</Text>
                        <Text fontSize="sm" color="blue.600" wordBreak="break-all" fontFamily="mono">
                          {shareableLink || (currentFileId ? `${window.location.origin}/${platform || (isShopify ? 'shopify' : 'non-shopify')}/product-onboarding?fileId=${currentFileId}` : 'Generating link...')}
                        </Text>
                      </Box>
                      <HStack spacing={2} wrap="wrap">
                        <Button
                          leftIcon={<CopyIcon />}
                          size="sm"
                          colorScheme="blue"
                          onClick={() => {
                            const linkToCopy = shareableLink || (currentFileId ? `${window.location.origin}/${platform || (isShopify ? 'shopify' : 'non-shopify')}/product-onboarding?fileId=${currentFileId}` : '');
                            if (linkToCopy) {
                              navigator.clipboard.writeText(linkToCopy);
                              toast({
                                title: 'Link copied!',
                                description: 'Shareable link copied to clipboard',
                                status: 'success',
                                duration: 2000,
                                isClosable: true,
                              });
                            }
                          }}
                          isDisabled={!shareableLink && !currentFileId}
                        >
                          Copy Link
                        </Button>
                        <Button
                          leftIcon={<FaEnvelope />}
                          size="sm"
                          colorScheme="orange"
                          onClick={() => {
                            const linkToShare = shareableLink || (currentFileId ? `${window.location.origin}/${platform || (isShopify ? 'shopify' : 'non-shopify')}/product-onboarding?fileId=${currentFileId}` : '');
                            if (linkToShare) {
                              const subject = encodeURIComponent('Product Onboarding Form');
                              const body = encodeURIComponent(`Please complete the product onboarding form:\n\n${linkToShare}`);
                              window.location.href = `mailto:?subject=${subject}&body=${body}`;
                            }
                          }}
                          isDisabled={!shareableLink && !currentFileId}
                        >
                          Email
                        </Button>
                        <Button
                          leftIcon={<FaWhatsapp />}
                          size="sm"
                          colorScheme="green"
                          onClick={() => {
                            const linkToShare = shareableLink || (currentFileId ? `${window.location.origin}/${platform || (isShopify ? 'shopify' : 'non-shopify')}/product-onboarding?fileId=${currentFileId}` : '');
                            if (linkToShare) {
                              const text = encodeURIComponent(`Please complete the product onboarding form:\n${linkToShare}`);
                              window.open(`https://wa.me/?text=${text}`, '_blank');
                            }
                          }}
                          isDisabled={!shareableLink && !currentFileId}
                        >
                          WhatsApp
                        </Button>
                        <Button
                          size="sm"
                          variant="outline"
                          onClick={() => {
                            setShareableLink(null);
                            setUpdateExisting(false);
                            setCurrentFileId(null);
                            setFileIdVerified(false); // Reset verification when creating new file
                            setShowShareSection(false);
                            setSearchParams({});
                          }}
                        >
                          Create New File
                        </Button>
                      </HStack>
                    </VStack>
                  </CardBody>
                </Card>
              )}

              {/* Products Table */}
              <Box 
                overflowX="auto" 
                overflowY="auto" 
                maxH="80vh"
                border="1px" 
                borderColor="gray.300" 
                bg="white" 
                shadow="sm"
                position="relative"
              >
                <Table variant="simple" size="sm" layout="auto" sx={{
                  'th, td': { borderColor: 'gray.300', borderWidth: '1px' },
                  'thead th': { 
                    position: 'sticky', 
                    top: 0, 
                    zIndex: 10
                  }
                }}>
                  <Thead bg="black">
                    <Tr>
                      {!isShopify && <Th color="white" fontSize="xs" py={3} px={2} whiteSpace="nowrap" fontWeight="bold" borderColor="black" bg="black">PRODUCT NAME <Text as="span" color="red.300">*</Text></Th>}
                      {isShopify && <Th color="white" fontSize="xs" py={3} px={2} whiteSpace="nowrap" fontWeight="bold" borderColor="black" bg="black">PRODUCT LINKS <Text as="span" color="red.300">*</Text></Th>}
                      {!isShopify && <Th color="white" fontSize="xs" py={3} px={2} whiteSpace="nowrap" fontWeight="bold" borderColor="black" bg="black">DESCRIPTION</Th>}
                      <Th color="white" fontSize="xs" py={3} px={2} whiteSpace="nowrap" fontWeight="bold" borderColor="black" bg="black">CATEGORY <Text as="span" color="red.300">*</Text></Th>
                      <Th color="white" fontSize="xs" py={3} px={2} whiteSpace="nowrap" fontWeight="bold" borderColor="black" bg="black">SUBCATEGORY <Text as="span" color="red.300">*</Text></Th>
                      <Th color="white" fontSize="xs" py={3} px={2} whiteSpace="nowrap" fontWeight="bold" borderColor="black" bg="black">SKU/ID</Th>
                      <Th color="white" fontSize="xs" py={3} px={2} whiteSpace="nowrap" fontWeight="bold" borderColor="black" bg="black">INVENTORY</Th>
                      <Th color="white" fontSize="xs" py={3} px={2} whiteSpace="nowrap" fontWeight="bold" borderColor="black" bg="black">LEAD TIME</Th>
                      <Th color="white" fontSize="xs" py={3} px={2} whiteSpace="nowrap" fontWeight="bold" borderColor="black" bg="black">PRICE <Text as="span" color="red.300">*</Text></Th>
                      <Th color="white" fontSize="xs" py={3} px={2} whiteSpace="nowrap" fontWeight="bold" borderColor="black" bg="black">QUANTITY</Th>
                      {/* Conditional Product Description Header - Show if any product has matching category */}
                      {products.some(p => shouldShowProductDetailsFields(p.category)) && (
                        <Th color="white" fontSize="xs" py={2} px={2} fontWeight="bold" borderColor="black" bg="green.600" verticalAlign="top">
                          <VStack spacing={0} align="start">
                            <Text>PRODUCT DESCRIPTION <Text as="span" color="red.300">*</Text></Text>
                            <Text fontSize="2xs" color="green.100" fontWeight="normal">Designer's perspective (20-30 words)</Text>
                          </VStack>
                        </Th>
                      )}
                      {/* Conditional Product Details Headers - Show if any product has matching category */}
                      {products.some(p => shouldShowProductDetailsFields(p.category)) && (
                        <>
                          {/* Show bag-specific headers if any product is a bag category */}
                          {products.some(p => shouldShowBagFields(p.category)) && !products.some(p => shouldShowHomeDecorFields(p.category)) && !products.some(p => shouldShowCandlesFragrancesFields(p.category)) && !products.some(p => shouldShowTablewareDiningFields(p.category)) ? (
                            <>
                              <Th color="white" fontSize="xs" py={2} px={2} fontWeight="bold" borderColor="black" bg="green.600" verticalAlign="top">
                                <VStack spacing={0} align="start">
                                  <Text>STYLE FEATURE 1 <Text as="span" color="red.300">*</Text></Text>
                                  <Text fontSize="2xs" color="green.100" fontWeight="normal">(Primary closure type)</Text>
                                </VStack>
                              </Th>
                              <Th color="white" fontSize="xs" py={2} px={2} fontWeight="bold" borderColor="black" bg="green.600" verticalAlign="top">
                                <VStack spacing={0} align="start">
                                  <Text>STYLE FEATURE 2 <Text as="span" color="red.300">*</Text></Text>
                                  <Text fontSize="2xs" color="green.100" fontWeight="normal">(Handle or strap type)</Text>
                                </VStack>
                              </Th>
                              <Th color="white" fontSize="xs" py={2} px={2} fontWeight="bold" borderColor="black" bg="green.600" verticalAlign="top">
                                <VStack spacing={0} align="start">
                                  <Text>STYLE FEATURE 3 <Text as="span" color="red.300">*</Text></Text>
                                  <Text fontSize="2xs" color="green.100" fontWeight="normal">(Interior organization/External pockets)</Text>
                                </VStack>
                              </Th>
                              <Th color="white" fontSize="xs" py={2} px={2} fontWeight="bold" borderColor="black" bg="green.600" verticalAlign="top">
                                <VStack spacing={0} align="start">
                                  <Text>STYLE FEATURE 4</Text>
                                  <Text fontSize="2xs" color="green.100" fontWeight="normal">(Special features or details)</Text>
                                </VStack>
                              </Th>
                              <Th color="white" fontSize="xs" py={2} px={2} fontWeight="bold" borderColor="black" bg="green.600" verticalAlign="top">
                                <VStack spacing={0} align="start">
                                  <Text>STYLE FEATURE 5</Text>
                                  <Text fontSize="2xs" color="green.100" fontWeight="normal">(Additional features)</Text>
                                </VStack>
                              </Th>
                              <Th color="white" fontSize="xs" py={2} px={2} fontWeight="bold" borderColor="black" bg="green.600" verticalAlign="top">
                                <VStack spacing={0} align="start">
                                  <Text>STYLE FEATURE 6</Text>
                                  <Text fontSize="2xs" color="green.100" fontWeight="normal">(Construction or finishing details)</Text>
                                </VStack>
                              </Th>
                              <Th color="white" fontSize="xs" py={2} px={2} fontWeight="bold" borderColor="black" bg="green.600" verticalAlign="top">
                                <VStack spacing={0} align="start">
                                  <Text>MATERIAL COMPOSITION <Text as="span" color="red.300">*</Text></Text>
                                  <Text fontSize="2xs" color="green.100" fontWeight="normal">(Primary material(s))</Text>
                                </VStack>
                              </Th>
                              <Th color="white" fontSize="xs" py={2} px={2} fontWeight="bold" borderColor="black" bg="green.600" verticalAlign="top">
                                <VStack spacing={0} align="start">
                                  <Text>LINING MATERIAL</Text>
                                  <Text fontSize="2xs" color="green.100" fontWeight="normal">(Interior lining material)</Text>
                                </VStack>
                              </Th>
                              <Th color="white" fontSize="xs" py={2} px={2} fontWeight="bold" borderColor="black" bg="green.600" verticalAlign="top">
                                <VStack spacing={0} align="start">
                                  <Text>HARDWARE FINISH <Text as="span" color="red.300">*</Text></Text>
                                </VStack>
                              </Th>
                              <Th color="white" fontSize="xs" py={2} px={2} fontWeight="bold" borderColor="black" bg="green.600" verticalAlign="top">
                                <VStack spacing={0} align="start">
                                  <Text>CARE INSTRUCTIONS <Text as="span" color="red.300">*</Text></Text>
                                </VStack>
                              </Th>
                              <Th color="white" fontSize="xs" py={2} px={2} fontWeight="bold" borderColor="black" bg="green.600" verticalAlign="top">
                                <VStack spacing={0} align="start">
                                  <Text>ADDITIONAL CARE NOTES</Text>
                                  <Text fontSize="2xs" color="green.100" fontWeight="normal">(Special care requirements)</Text>
                                </VStack>
                              </Th>
                              <Th color="white" fontSize="xs" py={2} px={2} fontWeight="bold" borderColor="black" bg="green.600" verticalAlign="top">
                                <VStack spacing={0} align="start">
                                  <Text>HANDLE TYPE <Text as="span" color="red.300">*</Text></Text>
                                </VStack>
                              </Th>
                              <Th color="white" fontSize="xs" py={2} px={2} fontWeight="bold" borderColor="black" bg="green.600" verticalAlign="top">
                                <VStack spacing={0} align="start">
                                  <Text>HANDLE/STRAP DROP</Text>
                                  <Text fontSize="2xs" color="green.100" fontWeight="normal">(Distance from top of bag to top of strap)</Text>
                                </VStack>
                              </Th>
                              <Th color="white" fontSize="xs" py={2} px={2} fontWeight="bold" borderColor="black" bg="green.600" verticalAlign="top">
                                <VStack spacing={0} align="start">
                                  <Text>STRAP - ADJUSTABLE? <Text as="span" color="red.300">*</Text></Text>
                                </VStack>
                              </Th>
                            </>
                          ) : products.some(p => shouldShowHomeDecorFields(p.category)) || products.some(p => shouldShowCandlesFragrancesFields(p.category)) || products.some(p => shouldShowTablewareDiningFields(p.category)) ? (
                            <>
                              {/* Home Decor, Candles & Fragrances, Tableware / Dining - Common Product Details headers */}
                              <Th color="white" fontSize="xs" py={2} px={2} fontWeight="bold" borderColor="black" bg="green.600" verticalAlign="top">
                                <VStack spacing={0} align="start">
                                  <Text>STYLE FEATURE 1 <Text as="span" color="red.300">*</Text></Text>
                                  <Text fontSize="2xs" color="green.100" fontWeight="normal">(Key design detail)</Text>
                                </VStack>
                              </Th>
                              <Th color="white" fontSize="xs" py={2} px={2} fontWeight="bold" borderColor="black" bg="green.600" verticalAlign="top">
                                <VStack spacing={0} align="start">
                                  <Text>STYLE FEATURE 2 <Text as="span" color="red.300">*</Text></Text>
                                  <Text fontSize="2xs" color="green.100" fontWeight="normal">(Key design detail)</Text>
                                </VStack>
                              </Th>
                              <Th color="white" fontSize="xs" py={2} px={2} fontWeight="bold" borderColor="black" bg="green.600" verticalAlign="top">
                                <VStack spacing={0} align="start">
                                  <Text>STYLE FEATURE 3 <Text as="span" color="red.300">*</Text></Text>
                                  <Text fontSize="2xs" color="green.100" fontWeight="normal">(Key design detail)</Text>
                                </VStack>
                              </Th>
                              <Th color="white" fontSize="xs" py={2} px={2} fontWeight="bold" borderColor="black" bg="green.600" verticalAlign="top">
                                <VStack spacing={0} align="start">
                                  <Text>STYLE FEATURE 4</Text>
                                  <Text fontSize="2xs" color="green.100" fontWeight="normal">(Key design detail)</Text>
                                </VStack>
                              </Th>
                              <Th color="white" fontSize="xs" py={2} px={2} fontWeight="bold" borderColor="black" bg="green.600" verticalAlign="top">
                                <VStack spacing={0} align="start">
                                  <Text>STYLE FEATURE 5</Text>
                                  <Text fontSize="2xs" color="green.100" fontWeight="normal">(Key design detail)</Text>
                                </VStack>
                              </Th>
                              <Th color="white" fontSize="xs" py={2} px={2} fontWeight="bold" borderColor="black" bg="green.600" verticalAlign="top">
                                <VStack spacing={0} align="start">
                                  <Text>STYLE FEATURE 6</Text>
                                  <Text fontSize="2xs" color="green.100" fontWeight="normal">(Key design detail)</Text>
                                </VStack>
                              </Th>
                              <Th color="white" fontSize="xs" py={2} px={2} fontWeight="bold" borderColor="black" bg="green.600" verticalAlign="top">
                                <VStack spacing={0} align="start">
                                  <Text>MATERIAL COMPOSITION <Text as="span" color="red.300">*</Text></Text>
                                  <Text fontSize="2xs" color="green.100" fontWeight="normal">(Primary material(s))</Text>
                                </VStack>
                              </Th>
                              <Th color="white" fontSize="xs" py={2} px={2} fontWeight="bold" borderColor="black" bg="green.600" verticalAlign="top">
                                <VStack spacing={0} align="start">
                                  <Text>CARE INSTRUCTIONS <Text as="span" color="red.300">*</Text></Text>
                                </VStack>
                              </Th>
                              <Th color="white" fontSize="xs" py={2} px={2} fontWeight="bold" borderColor="black" bg="green.600" verticalAlign="top">
                                <VStack spacing={0} align="start">
                                  <Text>ADDITIONAL CARE NOTES</Text>
                                  <Text fontSize="2xs" color="green.100" fontWeight="normal">(Special care requirements)</Text>
                                </VStack>
                              </Th>
                            </>
                          ) : (
                            <>
                              {/* Regular clothing fields */}
                              <Th color="white" fontSize="xs" py={2} px={2} fontWeight="bold" borderColor="black" bg="green.600" verticalAlign="top">
                                <VStack spacing={0} align="start">
                                  <Text>STYLE FEATURE 1 <Text as="span" color="red.300">*</Text></Text>
                                  <Text fontSize="2xs" color="green.100" fontWeight="normal">(Key design detail)</Text>
                                </VStack>
                              </Th>
                              <Th color="white" fontSize="xs" py={2} px={2} fontWeight="bold" borderColor="black" bg="green.600" verticalAlign="top">
                                <VStack spacing={0} align="start">
                                  <Text>STYLE FEATURE 2 <Text as="span" color="red.300">*</Text></Text>
                                  <Text fontSize="2xs" color="green.100" fontWeight="normal">(Closure type or feature)</Text>
                                </VStack>
                              </Th>
                              <Th color="white" fontSize="xs" py={2} px={2} fontWeight="bold" borderColor="black" bg="green.600" verticalAlign="top">
                                <VStack spacing={0} align="start">
                                  <Text>STYLE FEATURE 3 <Text as="span" color="red.300">*</Text></Text>
                                  <Text fontSize="2xs" color="green.100" fontWeight="normal">(Special features)</Text>
                                </VStack>
                              </Th>
                              <Th color="white" fontSize="xs" py={2} px={2} fontWeight="bold" borderColor="black" bg="green.600" verticalAlign="top">
                                <VStack spacing={0} align="start">
                                  <Text>STYLE FEATURE 4</Text>
                                  <Text fontSize="2xs" color="green.100" fontWeight="normal">(Additional features)</Text>
                                </VStack>
                              </Th>
                              <Th color="white" fontSize="xs" py={2} px={2} fontWeight="bold" borderColor="black" bg="green.600" verticalAlign="top">
                                <VStack spacing={0} align="start">
                                  <Text>STYLE FEATURE 5</Text>
                                  <Text fontSize="2xs" color="green.100" fontWeight="normal">(Length/fit detail)</Text>
                                </VStack>
                              </Th>
                              <Th color="white" fontSize="xs" py={2} px={2} fontWeight="bold" borderColor="black" bg="green.600" verticalAlign="top">
                                <VStack spacing={0} align="start">
                                  <Text>STYLE FEATURE 6</Text>
                                  <Text fontSize="2xs" color="green.100" fontWeight="normal">(Construction detail)</Text>
                                </VStack>
                              </Th>
                              <Th color="white" fontSize="xs" py={2} px={2} fontWeight="bold" borderColor="black" bg="green.600" verticalAlign="top">
                                <VStack spacing={0} align="start">
                                  <Text>MATERIAL COMPOSITION <Text as="span" color="red.300">*</Text></Text>
                                  <Text fontSize="2xs" color="green.100" fontWeight="normal">(Fabric content with)</Text>
                                </VStack>
                              </Th>
                              <Th color="white" fontSize="xs" py={2} px={2} fontWeight="bold" borderColor="black" bg="green.600" verticalAlign="top">
                                <VStack spacing={0} align="start">
                                  <Text>CARE INSTRUCTIONS <Text as="span" color="red.300">*</Text></Text>
                                </VStack>
                              </Th>
                              <Th color="white" fontSize="xs" py={2} px={2} fontWeight="bold" borderColor="black" bg="green.600" verticalAlign="top">
                                <VStack spacing={0} align="start">
                                  <Text>ADDITIONAL CARE NOTES</Text>
                                  <Text fontSize="2xs" color="green.100" fontWeight="normal">(Any special care requirements/Fabric origin)</Text>
                                </VStack>
                              </Th>
                              <Th color="white" fontSize="xs" py={2} px={2} fontWeight="bold" borderColor="black" bg="green.600" verticalAlign="top">
                                <VStack spacing={0} align="start">
                                  <Text>FIT TYPE <Text as="span" color="red.300">*</Text></Text>
                                </VStack>
                              </Th>
                              <Th color="white" fontSize="xs" py={2} px={2} fontWeight="bold" borderColor="black" bg="green.600" verticalAlign="top">
                                <VStack spacing={0} align="start">
                                  <Text>MODEL WEARING SIZE</Text>
                                </VStack>
                              </Th>
                              {/* Conditional Model Age Range Header - Show if any product has "Others" selected */}
                              {products.some(p => p.modelWearingSize === 'Others') && (
                                <Th color="white" fontSize="xs" py={2} px={2} fontWeight="bold" borderColor="black" bg="green.600" verticalAlign="top">
                                  <VStack spacing={0} align="start">
                                    <Text>MODEL AGE RANGE</Text>
                                    <Text fontSize="2xs" color="green.100" fontWeight="normal">(For kids - age range)</Text>
                                  </VStack>
                                </Th>
                              )}
                              <Th color="white" fontSize="xs" py={2} px={2} fontWeight="bold" borderColor="black" bg="green.600" verticalAlign="top">
                                <VStack spacing={0} align="start">
                                  <Text>MODEL MEASUREMENTS (CM)</Text>
                                  <Text fontSize="2xs" color="green.100" fontWeight="normal">(Height in centimeters)</Text>
                                </VStack>
                              </Th>
                              <Th color="white" fontSize="xs" py={2} px={2} fontWeight="bold" borderColor="black" bg="green.600" verticalAlign="top">
                                <VStack spacing={0} align="start">
                                  <Text>ADDITIONAL FIT NOTES</Text>
                                  <Text fontSize="2xs" color="green.100" fontWeight="normal">(Any additional sizing guidance)</Text>
                                </VStack>
                              </Th>
                            </>
                          )}
                        </>
                      )}
                      {/* Conditional Home Decor Headers - Show if any product is Home Decor */}
                      {products.some(p => shouldShowHomeDecorFields(p.category)) && (
                        <>
                          <Th color="white" fontSize="xs" py={2} px={2} fontWeight="bold" borderColor="black" bg="green.600" verticalAlign="top">
                            <VStack spacing={0} align="start">
                              <Text>COLOR/FINISH OPTIONS <Text as="span" color="red.300">*</Text></Text>
                              <Text fontSize="2xs" color="green.100" fontWeight="normal">(Available colors or finishes)</Text>
                            </VStack>
                          </Th>
                          <Th color="white" fontSize="xs" py={2} px={2} fontWeight="bold" borderColor="black" bg="green.600" verticalAlign="top">
                            <VStack spacing={0} align="start">
                              <Text>ORIGIN <Text as="span" color="red.300">*</Text></Text>
                              <Text fontSize="2xs" color="green.100" fontWeight="normal">(Design and manufacturing origin)</Text>
                            </VStack>
                          </Th>
                          <Th color="white" fontSize="xs" py={2} px={2} fontWeight="bold" borderColor="black" bg="green.600" verticalAlign="top">
                            <VStack spacing={0} align="start">
                              <Text>SET CONTENTS <Text as="span" color="red.300">*</Text></Text>
                              <Text fontSize="2xs" color="green.100" fontWeight="normal">(What's included - for sets only)</Text>
                            </VStack>
                          </Th>
                          <Th color="white" fontSize="xs" py={2} px={2} fontWeight="bold" borderColor="black" bg="green.600" verticalAlign="top">
                            <VStack spacing={0} align="start">
                              <Text>ADDITIONAL INFORMATION</Text>
                              <Text fontSize="2xs" color="green.100" fontWeight="normal">(Thread count, cushion info, etc.)</Text>
                            </VStack>
                          </Th>
                          <Th color="white" fontSize="xs" py={2} px={2} fontWeight="bold" borderColor="black" bg="green.600" verticalAlign="top">
                            <VStack spacing={0} align="start">
                              <Text>LIMITED EDITION INFO</Text>
                              <Text fontSize="2xs" color="green.100" fontWeight="normal">(Edition information if applicable)</Text>
                            </VStack>
                          </Th>
                          <Th color="white" fontSize="xs" py={2} px={2} fontWeight="bold" borderColor="black" bg="green.600" verticalAlign="top">
                            <VStack spacing={0} align="start">
                              <Text>FRAME MATERIAL</Text>
                              <Text fontSize="2xs" color="green.100" fontWeight="normal">(Frame material and finish if applicable)</Text>
                            </VStack>
                          </Th>
                          <Th color="white" fontSize="xs" py={2} px={2} fontWeight="bold" borderColor="black" bg="green.600" verticalAlign="top">
                            <VStack spacing={0} align="start">
                              <Text>PHOTO/ART SIZE CAPACITY</Text>
                              <Text fontSize="2xs" color="green.100" fontWeight="normal">(Picture frames only)</Text>
                            </VStack>
                          </Th>
                          <Th color="white" fontSize="xs" py={2} px={2} fontWeight="bold" borderColor="black" bg="green.600" verticalAlign="top">
                            <VStack spacing={0} align="start">
                              <Text>ORIENTATION</Text>
                              <Text fontSize="2xs" color="green.100" fontWeight="normal">(Picture frames only)</Text>
                            </VStack>
                          </Th>
                          <Th color="white" fontSize="xs" py={2} px={2} fontWeight="bold" borderColor="black" bg="green.600" verticalAlign="top">
                            <VStack spacing={0} align="start">
                              <Text>MOUNTING TYPE</Text>
                              <Text fontSize="2xs" color="green.100" fontWeight="normal">(Wall-mounted, freestanding, etc.)</Text>
                            </VStack>
                          </Th>
                        </>
                      )}
                      {/* Conditional Candles & Fragrances Headers - Show if any product is Candles & Fragrances */}
                      {products.some(p => shouldShowCandlesFragrancesFields(p.category)) && (
                        <>
                          <Th color="white" fontSize="xs" py={2} px={2} fontWeight="bold" borderColor="black" bg="green.600" verticalAlign="top">
                            <VStack spacing={0} align="start">
                              <Text>SCENT/FRAGRANCE</Text>
                              <Text fontSize="2xs" color="green.100" fontWeight="normal">(Scent description and notes)</Text>
                            </VStack>
                          </Th>
                          <Th color="white" fontSize="xs" py={2} px={2} fontWeight="bold" borderColor="black" bg="green.600" verticalAlign="top">
                            <VStack spacing={0} align="start">
                              <Text>COLOR/FINISH OPTIONS <Text as="span" color="red.300">*</Text></Text>
                              <Text fontSize="2xs" color="green.100" fontWeight="normal">(Candle/vessel color - use 'NO COLOR' if clear)</Text>
                            </VStack>
                          </Th>
                          <Th color="white" fontSize="xs" py={2} px={2} fontWeight="bold" borderColor="black" bg="green.600" verticalAlign="top">
                            <VStack spacing={0} align="start">
                              <Text>ORIGIN <Text as="span" color="red.300">*</Text></Text>
                              <Text fontSize="2xs" color="green.100" fontWeight="normal">(Design and manufacturing origin)</Text>
                            </VStack>
                          </Th>
                          <Th color="white" fontSize="xs" py={2} px={2} fontWeight="bold" borderColor="black" bg="green.600" verticalAlign="top">
                            <VStack spacing={0} align="start">
                              <Text>CAPACITY</Text>
                              <Text fontSize="2xs" color="green.100" fontWeight="normal">(Volume for liquid fragrances)</Text>
                            </VStack>
                          </Th>
                          <Th color="white" fontSize="xs" py={2} px={2} fontWeight="bold" borderColor="black" bg="green.600" verticalAlign="top">
                            <VStack spacing={0} align="start">
                              <Text>SET CONTENTS</Text>
                              <Text fontSize="2xs" color="green.100" fontWeight="normal">(What's included in fragrance sets)</Text>
                            </VStack>
                          </Th>
                          <Th color="white" fontSize="xs" py={2} px={2} fontWeight="bold" borderColor="black" bg="green.600" verticalAlign="top">
                            <VStack spacing={0} align="start">
                              <Text>BURNING DURATION</Text>
                              <Text fontSize="2xs" color="green.100" fontWeight="normal">(Candles only - Approximate burning duration)</Text>
                            </VStack>
                          </Th>
                          <Th color="white" fontSize="xs" py={2} px={2} fontWeight="bold" borderColor="black" bg="green.600" verticalAlign="top">
                            <VStack spacing={0} align="start">
                              <Text>NON-CANDLE PRODUCT TYPE</Text>
                              <Text fontSize="2xs" color="green.100" fontWeight="normal">(Non-Candle Product Type & Longevity/Duration)</Text>
                            </VStack>
                          </Th>
                          <Th color="white" fontSize="xs" py={2} px={2} fontWeight="bold" borderColor="black" bg="green.600" verticalAlign="top">
                            <VStack spacing={0} align="start">
                              <Text>USAGE INSTRUCTIONS</Text>
                              <Text fontSize="2xs" color="green.100" fontWeight="normal">(How to use the product)</Text>
                            </VStack>
                          </Th>
                          <Th color="white" fontSize="xs" py={2} px={2} fontWeight="bold" borderColor="black" bg="green.600" verticalAlign="top">
                            <VStack spacing={0} align="start">
                              <Text>SAFETY WARNINGS <Text as="span" color="red.300">*</Text></Text>
                              <Text fontSize="2xs" color="green.100" fontWeight="normal">(MANDATORY for all candles and fragrances)</Text>
                            </VStack>
                          </Th>
                        </>
                      )}
                      {/* Conditional Tableware / Dining Headers - Show if any product is Tableware / Dining */}
                      {products.some(p => shouldShowTablewareDiningFields(p.category)) && (
                        <>
                          <Th color="white" fontSize="xs" py={2} px={2} fontWeight="bold" borderColor="black" bg="green.600" verticalAlign="top">
                            <VStack spacing={0} align="start">
                              <Text>COLOR/FINISH OPTIONS <Text as="span" color="red.300">*</Text></Text>
                              <Text fontSize="2xs" color="green.100" fontWeight="normal">(Available colors or finishes - use 'NO COLOR' if clear)</Text>
                            </VStack>
                          </Th>
                          <Th color="white" fontSize="xs" py={2} px={2} fontWeight="bold" borderColor="black" bg="green.600" verticalAlign="top">
                            <VStack spacing={0} align="start">
                              <Text>ORIGIN <Text as="span" color="red.300">*</Text></Text>
                              <Text fontSize="2xs" color="green.100" fontWeight="normal">(Design and manufacturing origin)</Text>
                            </VStack>
                          </Th>
                          <Th color="white" fontSize="xs" py={2} px={2} fontWeight="bold" borderColor="black" bg="green.600" verticalAlign="top">
                            <VStack spacing={0} align="start">
                              <Text>CAPACITY</Text>
                              <Text fontSize="2xs" color="green.100" fontWeight="normal">(Containers, glasses, mugs, bowls in ml or litres)</Text>
                            </VStack>
                          </Th>
                          <Th color="white" fontSize="xs" py={2} px={2} fontWeight="bold" borderColor="black" bg="green.600" verticalAlign="top">
                            <VStack spacing={0} align="start">
                              <Text>SET CONTENTS</Text>
                              <Text fontSize="2xs" color="green.100" fontWeight="normal">(What's included - specific breakdown for sets)</Text>
                            </VStack>
                          </Th>
                          <Th color="white" fontSize="xs" py={2} px={2} fontWeight="bold" borderColor="black" bg="green.600" verticalAlign="top">
                            <VStack spacing={0} align="start">
                              <Text>SAFETY WARNINGS <Text as="span" color="red.300">*</Text></Text>
                              <Text fontSize="2xs" color="green.100" fontWeight="normal">(REQUIRED for fragile items)</Text>
                            </VStack>
                          </Th>
                          <Th color="white" fontSize="xs" py={2} px={2} fontWeight="bold" borderColor="black" bg="green.600" verticalAlign="top">
                            <VStack spacing={0} align="start">
                              <Text>FOOD SAFE CERTIFICATION</Text>
                              <Text fontSize="2xs" color="green.100" fontWeight="normal">(Food safety certification or standards)</Text>
                            </VStack>
                          </Th>
                        </>
                      )}
                      {!isShopify && <Th color="white" fontSize="xs" py={3} px={2} whiteSpace="nowrap" fontWeight="bold" borderColor="black" bg="black">SIZE</Th>}
                      {!isShopify && <Th color="white" fontSize="xs" py={3} px={2} whiteSpace="nowrap" fontWeight="bold" borderColor="black" bg="black">COLOR</Th>}
                      {!isShopify && <Th color="white" fontSize="xs" py={3} px={2} whiteSpace="nowrap" fontWeight="bold" borderColor="black" bg="black">PATTERN</Th>}
                      <Th color="white" fontSize="xs" py={3} px={2} whiteSpace="nowrap" fontWeight="bold" borderColor="black" bg="black">WEIGHT (grams/kg)</Th>
                      <Th color="white" fontSize="xs" py={3} px={2} whiteSpace="nowrap" fontWeight="bold" borderColor="black" bg="black">LENGTH (cm)</Th>
                      <Th color="white" fontSize="xs" py={3} px={2} whiteSpace="nowrap" fontWeight="bold" borderColor="black" bg="black">WIDTH (cm)</Th>
                      <Th color="white" fontSize="xs" py={3} px={2} whiteSpace="nowrap" fontWeight="bold" borderColor="black" bg="black">HEIGHT (cm)</Th>
                      {!isShopify && <Th color="white" fontSize="xs" py={3} px={2} whiteSpace="nowrap" fontWeight="bold" borderColor="black" bg="black">MAIN IMAGE</Th>}
                      {!isShopify && <Th color="white" fontSize="xs" py={3} px={2} whiteSpace="nowrap" fontWeight="bold" borderColor="black" bg="black">ADDITIONAL IMAGES</Th>}
                      {!isShopify && <Th color="white" fontSize="xs" py={3} px={2} whiteSpace="nowrap" fontWeight="bold" borderColor="black" bg="black">NOTES</Th>}
                      <Th color="white" fontSize="xs" py={3} px={2} whiteSpace="nowrap" fontWeight="bold" borderColor="black" bg="black">PACKAGING</Th>
                    </Tr>
                  </Thead>
                  <Tbody>
                    {products.map((product, index) => (
                      <Tr key={product.id} _hover={{ bg: "blue.50" }} bg={index % 2 === 0 ? "white" : "gray.50"}>
                        {!isShopify && (
                          <Td p={2} borderColor="gray.300">
                            <HStack spacing={1}>
                              <IconButton
                                icon={<DeleteIcon />}
                                size="xs"
                                colorScheme="red"
                                variant="ghost"
                                onClick={() => removeProduct(index)}
                                aria-label="Remove row"
                                minW="auto"
                              />
                              <Input
                                size="sm"
                                w="160px"
                                value={product.productName}
                                onChange={(e) => handleProductChange(index, 'productName', e.target.value)}
                                placeholder="Enter product name"
                                bg="white"
                                border="1px solid"
                                borderColor={errors[`productName_${index}`] ? "red.500" : "gray.300"}
                                _hover={{ borderColor: errors[`productName_${index}`] ? "red.500" : "gray.400" }}
                                _focus={{ borderColor: errors[`productName_${index}`] ? "red.500" : "blue.500", boxShadow: errors[`productName_${index}`] ? "0 0 0 1px red.500" : "0 0 0 1px blue.500" }}
                              />
                            </HStack>
                          </Td>
                        )}

                        {isShopify && (
                          <Td p={2} borderColor="gray.300">
                            <HStack spacing={1}>
                              <IconButton
                                icon={<DeleteIcon />}
                                size="xs"
                                colorScheme="red"
                                variant="ghost"
                                onClick={() => removeProduct(index)}
                                aria-label="Remove row"
                                minW="auto"
                              />
                              <Input
                                size="sm"
                                w="180px"
                                value={product.productLinks}
                                onChange={(e) => handleProductChange(index, 'productLinks', e.target.value)}
                                placeholder="Enter product links"
                                bg="white"
                                border="1px solid"
                                borderColor={errors[`productLinks_${index}`] ? "red.500" : "gray.300"}
                                _hover={{ borderColor: errors[`productLinks_${index}`] ? "red.500" : "gray.400" }}
                                _focus={{ borderColor: errors[`productLinks_${index}`] ? "red.500" : "blue.500", boxShadow: errors[`productLinks_${index}`] ? "0 0 0 1px red.500" : "0 0 0 1px blue.500" }}
                              />
                            </HStack>
                          </Td>
                        )}

                        {!isShopify && (
                          <Td p={2} borderColor="gray.300">
                            <Textarea
                              size="sm"
                              fontSize="xs"
                              value={product.description}
                              onChange={(e) => handleProductChange(index, 'description', e.target.value)}
                              placeholder="Enter product description"
                              rows={3}
                              w="180px"
                              bg="white"
                              border="1px solid"
                              borderColor="gray.300"
                              _hover={{ borderColor: "gray.400" }}
                              _focus={{ borderColor: "blue.500", boxShadow: "0 0 0 1px blue.500" }}
                            />
                          </Td>
                        )}

                        <Td p={2} borderColor="gray.300">
                          <Select
                            size="sm"
                            w="140px"
                            value={product.category}
                            onChange={(e) => {
                              handleProductChange(index, 'category', e.target.value);
                              handleProductChange(index, 'subcategory', '');
                              handleProductChange(index, 'size', '');
                            }}
                            placeholder="Select category"
                            bg="white"
                            border="1px solid"
                            borderColor={errors[`category_${index}`] ? "red.500" : "gray.300"}
                            _hover={{ borderColor: errors[`category_${index}`] ? "red.500" : "gray.400" }}
                            _focus={{ borderColor: errors[`category_${index}`] ? "red.500" : "blue.500", boxShadow: errors[`category_${index}`] ? "0 0 0 1px red.500" : "0 0 0 1px blue.500" }}
                          >
                            {Object.keys(categories).map(cat => (
                              <option key={cat} value={cat}>{cat}</option>
                            ))}
                          </Select>
                        </Td>

                        <Td p={2} borderColor="gray.300">
                          <Select
                            size="sm"
                            w="150px"
                            value={product.subcategory}
                            onChange={(e) => {
                              handleProductChange(index, 'subcategory', e.target.value);
                              // Clear size if it depends on subcategory (e.g., Other Accessories)
                              if (product.category === 'Other Accessories' || product.category === 'Home Decor' || product.category === 'Tableware / Dining' || product.category === 'Candles & Fragrances') {
                                handleProductChange(index, 'size', '');
                              }
                            }}
                            placeholder="Select subcategory"
                            isDisabled={!product.category}
                            bg="white"
                            border="1px solid"
                            borderColor={errors[`subcategory_${index}`] ? "red.500" : "gray.300"}
                            _hover={{ borderColor: errors[`subcategory_${index}`] ? "red.500" : "gray.400" }}
                            _focus={{ borderColor: errors[`subcategory_${index}`] ? "red.500" : "blue.500", boxShadow: errors[`subcategory_${index}`] ? "0 0 0 1px red.500" : "0 0 0 1px blue.500" }}
                          >
                            {product.category && categories[product.category]?.map(subcat => (
                              <option key={subcat} value={subcat}>{subcat}</option>
                            ))}
                          </Select>
                        </Td>

                        <Td p={2} borderColor="gray.300">
                          <Input
                            size="sm"
                            w="120px"
                            value={product.sku}
                            onChange={(e) => handleProductChange(index, 'sku', e.target.value)}
                            placeholder="Enter SKU/ID"
                            bg="white"
                            border="1px solid"
                            borderColor="gray.300"
                            _hover={{ borderColor: "gray.400" }}
                            _focus={{ borderColor: "blue.500", boxShadow: "0 0 0 1px blue.500" }}
                          />
                        </Td>

                        <Td p={2} borderColor="gray.300">
                          <Select
                            size="sm"
                            w="180px"
                            value={product.inventoryAvailability}
                            onChange={(e) => {
                              handleProductChange(index, 'inventoryAvailability', e.target.value);
                              if (e.target.value !== 'Made to Order') {
                                handleProductChange(index, 'leadTime', '');
                              }
                            }}
                            placeholder="Select availability"
                            bg="white"
                            border="1px solid"
                            borderColor="gray.300"
                            _hover={{ borderColor: "gray.400" }}
                            _focus={{ borderColor: "blue.500", boxShadow: "0 0 0 1px blue.500" }}
                          >
                            <option value="Readily Available for Delivery">Ready to Ship</option>
                            <option value="Made to Order">Made to Order</option>
                            <option value="Sold out and will not be restocked">Sold out and will not be restocked</option>
                          </Select>
                        </Td>

                        <Td p={2} borderColor="gray.300">
                          <NumberInput
                            size="sm"
                            value={product.leadTime}
                            onChange={(value) => handleProductChange(index, 'leadTime', value)}
                            isDisabled={product.inventoryAvailability !== 'Made to Order'}
                            min={0}
                            w="70px"
                            maxW="70px"
                          >
                            <NumberInputField 
                              placeholder="Days" 
                              bg="white"
                              border="1px solid"
                              borderColor="gray.300"
                              _hover={{ borderColor: "gray.400" }}
                              _focus={{ borderColor: "blue.500", boxShadow: "0 0 0 1px blue.500" }}
                            />
                          </NumberInput>
                        </Td>

                        <Td p={2} borderColor="gray.300" pl={4}>
                          <Input
                            size="sm"
                            w="80px"
                            value={product.price}
                            onChange={(e) => handleProductChange(index, 'price', e.target.value)}
                            placeholder="Price"
                            bg="white"
                            border="1px solid"
                            borderColor={errors[`price_${index}`] ? "red.500" : "gray.300"}
                            _hover={{ borderColor: errors[`price_${index}`] ? "red.500" : "gray.400" }}
                            _focus={{ borderColor: errors[`price_${index}`] ? "red.500" : "blue.500", boxShadow: errors[`price_${index}`] ? "0 0 0 1px red.500" : "0 0 0 1px blue.500" }}
                          />
                        </Td>

                        <Td p={2} borderColor="gray.300">
                          <Input
                            size="sm"
                            w="70px"
                            value={product.quantity}
                            onChange={(e) => handleProductChange(index, 'quantity', e.target.value)}
                            placeholder="Quantity"
                            bg="white"
                            border="1px solid"
                            borderColor="gray.300"
                            _hover={{ borderColor: "gray.400" }}
                            _focus={{ borderColor: "blue.500", boxShadow: "0 0 0 1px blue.500" }}
                          />
                        </Td>

                        {/* Conditional Product Description Cell - Show only if category matches */}
                        {shouldShowProductDetailsFields(product.category) && (
                          <Td p={2} borderColor="gray.300" bg="green.50" whiteSpace="normal" wordBreak="break-word">
                            <Textarea
                              size="sm"
                              fontSize="xs"
                              w="200px"
                              value={product.productDescription}
                              onChange={(e) => handleProductChange(index, 'productDescription', e.target.value)}
                              placeholder={shouldShowBagFields(product.category) 
                                ? "Example: A buttery-soft pebbled leather construction adds a luxe edge to this petite convertible tote. (20-30 words)"
                                : "Designer's perspective (20-30 words)"}
                              rows={4}
                              bg="white"
                              border="1px solid"
                              borderColor={errors[`productDescription_${index}`] ? "red.500" : "gray.300"}
                              _hover={{ borderColor: errors[`productDescription_${index}`] ? "red.500" : "gray.400" }}
                              _focus={{ borderColor: errors[`productDescription_${index}`] ? "red.500" : "blue.500", boxShadow: errors[`productDescription_${index}`] ? "0 0 0 1px red.500" : "0 0 0 1px blue.500" }}
                              resize="vertical"
                            />
                          </Td>
                        )}

                        {/* Empty cell if category doesn't match but headers are shown */}
                        {!shouldShowProductDetailsFields(product.category) && products.some(p => shouldShowProductDetailsFields(p.category)) && (
                          <Td p={2} borderColor="gray.300" bg="gray.100"></Td>
                        )}

                        {/* Conditional Product Details Cells - Show only if category matches */}
                        {shouldShowProductDetailsFields(product.category) && (
                          <>
                            {/* Show bag-specific fields if category is Bags or Kids Accessories */}
                            {shouldShowBagFields(product.category) && !shouldShowHomeDecorFields(product.category) && !shouldShowCandlesFragrancesFields(product.category) && !shouldShowTablewareDiningFields(product.category) ? (
                              <>
                                {/* Style Feature 1 - Primary closure type */}
                                <Td p={2} borderColor="gray.300" bg="green.50" whiteSpace="normal" wordBreak="break-word">
                                  <Textarea
                                    size="sm"
                                    fontSize="xs"
                                    w="150px"
                                    minH="80px"
                                    value={product.styleFeature1}
                                    onChange={(e) => handleProductChange(index, 'styleFeature1', e.target.value)}
                                    placeholder="Example: Top zip / Magnetic snap closure (word limit: 3-8)"
                                    rows={3}
                                    bg="white"
                                    border="1px solid"
                                    borderColor={errors[`styleFeature1_${index}`] ? "red.500" : "gray.300"}
                                    _hover={{ borderColor: errors[`styleFeature1_${index}`] ? "red.500" : "gray.400" }}
                                    _focus={{ borderColor: errors[`styleFeature1_${index}`] ? "red.500" : "blue.500", boxShadow: errors[`styleFeature1_${index}`] ? "0 0 0 1px red.500" : "0 0 0 1px blue.500" }}
                                    resize="vertical"
                                  />
                                </Td>

                                {/* Style Feature 2 - Handle or strap type */}
                                <Td p={2} borderColor="gray.300" bg="green.50" whiteSpace="normal" wordBreak="break-word">
                                  <Textarea
                                    size="sm"
                                    fontSize="xs"
                                    w="150px"
                                    minH="80px"
                                    value={product.styleFeature2}
                                    onChange={(e) => handleProductChange(index, 'styleFeature2', e.target.value)}
                                    placeholder="Example: Side handle / Dual top handles (word limit: 3-8)"
                                    rows={3}
                                    bg="white"
                                    border="1px solid"
                                    borderColor={errors[`styleFeature2_${index}`] ? "red.500" : "gray.300"}
                                    _hover={{ borderColor: errors[`styleFeature2_${index}`] ? "red.500" : "gray.400" }}
                                    _focus={{ borderColor: errors[`styleFeature2_${index}`] ? "red.500" : "blue.500", boxShadow: errors[`styleFeature2_${index}`] ? "0 0 0 1px red.500" : "0 0 0 1px blue.500" }}
                                    resize="vertical"
                                  />
                                </Td>

                                {/* Style Feature 3 - Interior organization/External pockets */}
                                <Td p={2} borderColor="gray.300" bg="green.50" whiteSpace="normal" wordBreak="break-word">
                                  <Textarea
                                    size="sm"
                                    fontSize="xs"
                                    w="150px"
                                    minH="80px"
                                    value={product.styleFeature3}
                                    onChange={(e) => handleProductChange(index, 'styleFeature3', e.target.value)}
                                    placeholder="Example: One interior compartment / Front zip pocket (word limit: 8-15)"
                                    rows={3}
                                    bg="white"
                                    border="1px solid"
                                    borderColor={errors[`styleFeature3_${index}`] ? "red.500" : "gray.300"}
                                    _hover={{ borderColor: errors[`styleFeature3_${index}`] ? "red.500" : "gray.400" }}
                                    _focus={{ borderColor: errors[`styleFeature3_${index}`] ? "red.500" : "blue.500", boxShadow: errors[`styleFeature3_${index}`] ? "0 0 0 1px red.500" : "0 0 0 1px blue.500" }}
                                    resize="vertical"
                                  />
                                </Td>

                                {/* Style Feature 4 - Special features or details */}
                                <Td p={2} borderColor="gray.300" bg="green.50" whiteSpace="normal" wordBreak="break-word">
                                  <Textarea
                                    size="sm"
                                    fontSize="xs"
                                    w="150px"
                                    minH="80px"
                                    value={product.styleFeature4}
                                    onChange={(e) => handleProductChange(index, 'styleFeature4', e.target.value)}
                                    placeholder="Example: Convertible design / Removable strap (word limit: 5-10)"
                                    rows={3}
                                    bg="white"
                                    border="1px solid"
                                    borderColor="gray.300"
                                    _hover={{ borderColor: "gray.400" }}
                                    _focus={{ borderColor: "blue.500", boxShadow: "0 0 0 1px blue.500" }}
                                    resize="vertical"
                                  />
                                </Td>

                                {/* Style Feature 5 - Additional features */}
                                <Td p={2} borderColor="gray.300" bg="green.50" whiteSpace="normal" wordBreak="break-word">
                                  <Textarea
                                    size="sm"
                                    fontSize="xs"
                                    w="150px"
                                    minH="80px"
                                    value={product.styleFeature5}
                                    onChange={(e) => handleProductChange(index, 'styleFeature5', e.target.value)}
                                    placeholder="Example: Adjustable strap / Interior lining (word limit: 5-10)"
                                    rows={3}
                                    bg="white"
                                    border="1px solid"
                                    borderColor="gray.300"
                                    _hover={{ borderColor: "gray.400" }}
                                    _focus={{ borderColor: "blue.500", boxShadow: "0 0 0 1px blue.500" }}
                                    resize="vertical"
                                  />
                                </Td>

                                {/* Style Feature 6 - Construction or finishing details */}
                                <Td p={2} borderColor="gray.300" bg="green.50" whiteSpace="normal" wordBreak="break-word">
                                  <Textarea
                                    size="sm"
                                    fontSize="xs"
                                    w="150px"
                                    minH="80px"
                                    value={product.styleFeature6}
                                    onChange={(e) => handleProductChange(index, 'styleFeature6', e.target.value)}
                                    placeholder="Example: Contrast stitching / Quilted design (word limit: 5-10)"
                                    rows={3}
                                    bg="white"
                                    border="1px solid"
                                    borderColor="gray.300"
                                    _hover={{ borderColor: "gray.400" }}
                                    _focus={{ borderColor: "blue.500", boxShadow: "0 0 0 1px blue.500" }}
                                    resize="vertical"
                                  />
                                </Td>

                                {/* Material Composition */}
                                <Td p={2} borderColor="gray.300" bg="green.50" whiteSpace="normal" wordBreak="break-word">
                                  <Textarea
                                    size="sm"
                                    fontSize="xs"
                                    w="150px"
                                    minH="80px"
                                    value={product.materialComposition}
                                    onChange={(e) => handleProductChange(index, 'materialComposition', e.target.value)}
                                    placeholder="Example: Leather / Pebbled leather / Vegan leather (word limit: 3-10)"
                                    rows={3}
                                    bg="white"
                                    border="1px solid"
                                    borderColor={errors[`materialComposition_${index}`] ? "red.500" : "gray.300"}
                                    _hover={{ borderColor: errors[`materialComposition_${index}`] ? "red.500" : "gray.400" }}
                                    _focus={{ borderColor: errors[`materialComposition_${index}`] ? "red.500" : "blue.500", boxShadow: errors[`materialComposition_${index}`] ? "0 0 0 1px red.500" : "0 0 0 1px blue.500" }}
                                    resize="vertical"
                                  />
                                </Td>

                                {/* Lining Material */}
                                <Td p={2} borderColor="gray.300" bg="green.50" whiteSpace="normal" wordBreak="break-word">
                                  <Textarea
                                    size="sm"
                                    fontSize="xs"
                                    w="150px"
                                    minH="80px"
                                    value={product.liningMaterial}
                                    onChange={(e) => handleProductChange(index, 'liningMaterial', e.target.value)}
                                    placeholder="Example: Fabric lining / Suede lining / Unlined (word limit: 3-8)"
                                    rows={3}
                                    bg="white"
                                    border="1px solid"
                                    borderColor="gray.300"
                                    _hover={{ borderColor: "gray.400" }}
                                    _focus={{ borderColor: "blue.500", boxShadow: "0 0 0 1px blue.500" }}
                                    resize="vertical"
                                  />
                                </Td>

                                {/* Hardware Finish */}
                                <Td p={2} borderColor="gray.300" bg="green.50">
                                  <Select
                                    size="sm"
                                    w="150px"
                                    value={product.hardwareFinish}
                                    onChange={(e) => handleProductChange(index, 'hardwareFinish', e.target.value)}
                                    placeholder="Select hardware finish"
                                    bg="white"
                                    border="1px solid"
                                    borderColor={errors[`hardwareFinish_${index}`] ? "red.500" : "gray.300"}
                                    _hover={{ borderColor: errors[`hardwareFinish_${index}`] ? "red.500" : "gray.400" }}
                                    _focus={{ borderColor: errors[`hardwareFinish_${index}`] ? "red.500" : "blue.500", boxShadow: errors[`hardwareFinish_${index}`] ? "0 0 0 1px red.500" : "0 0 0 1px blue.500" }}
                                  >
                                    <option value="Gold-tone">Gold-tone</option>
                                    <option value="Silver-tone">Silver-tone</option>
                                    <option value="Rose gold-tone">Rose gold-tone</option>
                                    <option value="Brass">Brass</option>
                                    <option value="Gunmetal">Gunmetal</option>
                                    <option value="Black">Black</option>
                                    <option value="Antique brass">Antique brass</option>
                                    <option value="Mixed metals">Mixed metals</option>
                                    <option value="No hardware">No hardware</option>
                                  </Select>
                                </Td>

                                {/* Care Instructions */}
                                <Td p={2} borderColor="gray.300" bg="green.50">
                                  <Select
                                    size="sm"
                                    w="150px"
                                    value={product.careInstructions}
                                    onChange={(e) => handleProductChange(index, 'careInstructions', e.target.value)}
                                    placeholder="Select care instructions"
                                    bg="white"
                                    border="1px solid"
                                    borderColor={errors[`careInstructions_${index}`] ? "red.500" : "gray.300"}
                                    _hover={{ borderColor: errors[`careInstructions_${index}`] ? "red.500" : "gray.400" }}
                                    _focus={{ borderColor: errors[`careInstructions_${index}`] ? "red.500" : "blue.500", boxShadow: errors[`careInstructions_${index}`] ? "0 0 0 1px red.500" : "0 0 0 1px blue.500" }}
                                  >
                                    <option value="Wipe clean with damp cloth">Wipe clean with damp cloth</option>
                                    <option value="Spot clean only">Spot clean only</option>
                                    <option value="Professional leather care recommended">Professional leather care recommended</option>
                                    <option value="Hand wash (fabric bags)">Hand wash (fabric bags)</option>
                                    <option value="Avoid water exposure">Avoid water exposure</option>
                                  </Select>
                                </Td>

                                {/* Additional Care Notes */}
                                <Td p={2} borderColor="gray.300" bg="green.50" whiteSpace="normal" wordBreak="break-word">
                                  <Textarea
                                    size="sm"
                                    fontSize="xs"
                                    w="180px"
                                    minH="80px"
                                    value={product.additionalCareNotes}
                                    onChange={(e) => handleProductChange(index, 'additionalCareNotes', e.target.value)}
                                    placeholder="Example: Avoid prolonged sun exposure / Store in dust bag (word limit: 10-15)"
                                    rows={3}
                                    bg="white"
                                    border="1px solid"
                                    borderColor="gray.300"
                                    _hover={{ borderColor: "gray.400" }}
                                    _focus={{ borderColor: "blue.500", boxShadow: "0 0 0 1px blue.500" }}
                                    resize="vertical"
                                  />
                                </Td>

                                {/* Handle Type */}
                                <Td p={2} borderColor="gray.300" bg="green.50">
                                  <Select
                                    size="sm"
                                    w="150px"
                                    value={product.handleType}
                                    onChange={(e) => handleProductChange(index, 'handleType', e.target.value)}
                                    placeholder="Select handle type"
                                    bg="white"
                                    border="1px solid"
                                    borderColor={errors[`handleType_${index}`] ? "red.500" : "gray.300"}
                                    _hover={{ borderColor: errors[`handleType_${index}`] ? "red.500" : "gray.400" }}
                                    _focus={{ borderColor: errors[`handleType_${index}`] ? "red.500" : "blue.500", boxShadow: errors[`handleType_${index}`] ? "0 0 0 1px red.500" : "0 0 0 1px blue.500" }}
                                  >
                                    <option value="Top handles">Top handles</option>
                                    <option value="Shoulder strap">Shoulder strap</option>
                                    <option value="Crossbody strap">Crossbody strap</option>
                                    <option value="Top handles + removable crossbody strap">Top handles + removable crossbody strap</option>
                                    <option value="Top handles + shoulder strap">Top handles + shoulder strap</option>
                                    <option value="Clutch (no handles)">Clutch (no handles)</option>
                                    <option value="Backpack straps">Backpack straps</option>
                                    <option value="Belt loop">Belt loop</option>
                                    <option value="Wristlet strap">Wristlet strap</option>
                                  </Select>
                                </Td>

                                {/* Handle/Strap Drop */}
                                <Td p={2} borderColor="gray.300" bg="green.50">
                                  <Input
                                    size="sm"
                                    w="150px"
                                    value={product.handleStrapDrop}
                                    onChange={(e) => handleProductChange(index, 'handleStrapDrop', e.target.value)}
                                    placeholder="Example: 22cm drop"
                                    bg="white"
                                    border="1px solid"
                                    borderColor="gray.300"
                                    _hover={{ borderColor: "gray.400" }}
                                    _focus={{ borderColor: "blue.500", boxShadow: "0 0 0 1px blue.500" }}
                                  />
                                </Td>

                                {/* Strap - Adjustable? */}
                                <Td p={2} borderColor="gray.300" bg="green.50">
                                  <Select
                                    size="sm"
                                    w="150px"
                                    value={product.strapAdjustable}
                                    onChange={(e) => handleProductChange(index, 'strapAdjustable', e.target.value)}
                                    placeholder="Select option"
                                    bg="white"
                                    border="1px solid"
                                    borderColor={errors[`strapAdjustable_${index}`] ? "red.500" : "gray.300"}
                                    _hover={{ borderColor: errors[`strapAdjustable_${index}`] ? "red.500" : "gray.400" }}
                                    _focus={{ borderColor: errors[`strapAdjustable_${index}`] ? "red.500" : "blue.500", boxShadow: errors[`strapAdjustable_${index}`] ? "0 0 0 1px red.500" : "0 0 0 1px blue.500" }}
                                  >
                                    <option value="Yes - Adjustable">Yes - Adjustable</option>
                                    <option value="Yes - Adjustable and removable">Yes - Adjustable and removable</option>
                                    <option value="Not applicable">Not applicable</option>
                                  </Select>
                                </Td>
                              </>
                            ) : shouldShowHomeDecorFields(product.category) || shouldShowCandlesFragrancesFields(product.category) || shouldShowTablewareDiningFields(product.category) ? (
                              <>
                                {/* Home Decor, Candles & Fragrances, Tableware / Dining - Show common Product Details fields */}
                                {/* Style Feature 1 */}
                                <Td p={2} borderColor="gray.300" bg="green.50" whiteSpace="normal" wordBreak="break-word">
                                  <Textarea
                                    size="sm"
                                    fontSize="xs"
                                    w="150px"
                                    minH="80px"
                                    value={product.styleFeature1}
                                    onChange={(e) => handleProductChange(index, 'styleFeature1', e.target.value)}
                                    placeholder="Example: Crewneck (word limit: 8-10)"
                                    rows={3}
                                    bg="white"
                                    border="1px solid"
                                    borderColor={errors[`styleFeature1_${index}`] ? "red.500" : "gray.300"}
                                    _hover={{ borderColor: errors[`styleFeature1_${index}`] ? "red.500" : "gray.400" }}
                                    _focus={{ borderColor: errors[`styleFeature1_${index}`] ? "red.500" : "blue.500", boxShadow: errors[`styleFeature1_${index}`] ? "0 0 0 1px red.500" : "0 0 0 1px blue.500" }}
                                    resize="vertical"
                                  />
                                </Td>

                                {/* Style Feature 2 */}
                                <Td p={2} borderColor="gray.300" bg="green.50" whiteSpace="normal" wordBreak="break-word">
                                  <Textarea
                                    size="sm"
                                    fontSize="xs"
                                    w="150px"
                                    minH="80px"
                                    value={product.styleFeature2}
                                    onChange={(e) => handleProductChange(index, 'styleFeature2', e.target.value)}
                                    placeholder="Example: Pulls over (word limit: 8-10)"
                                    rows={3}
                                    bg="white"
                                    border="1px solid"
                                    borderColor={errors[`styleFeature2_${index}`] ? "red.500" : "gray.300"}
                                    _hover={{ borderColor: errors[`styleFeature2_${index}`] ? "red.500" : "gray.400" }}
                                    _focus={{ borderColor: errors[`styleFeature2_${index}`] ? "red.500" : "blue.500", boxShadow: errors[`styleFeature2_${index}`] ? "0 0 0 1px red.500" : "0 0 0 1px blue.500" }}
                                    resize="vertical"
                                  />
                                </Td>

                                {/* Style Feature 3 */}
                                <Td p={2} borderColor="gray.300" bg="green.50" whiteSpace="normal" wordBreak="break-word">
                                  <Textarea
                                    size="sm"
                                    fontSize="xs"
                                    w="150px"
                                    minH="80px"
                                    value={product.styleFeature3}
                                    onChange={(e) => handleProductChange(index, 'styleFeature3', e.target.value)}
                                    placeholder="Example: Hand-beaded detailing (word limit: 8-10)"
                                    rows={3}
                                    bg="white"
                                    border="1px solid"
                                    borderColor={errors[`styleFeature3_${index}`] ? "red.500" : "gray.300"}
                                    _hover={{ borderColor: errors[`styleFeature3_${index}`] ? "red.500" : "gray.400" }}
                                    _focus={{ borderColor: errors[`styleFeature3_${index}`] ? "red.500" : "blue.500", boxShadow: errors[`styleFeature3_${index}`] ? "0 0 0 1px red.500" : "0 0 0 1px blue.500" }}
                                    resize="vertical"
                                  />
                                </Td>

                                {/* Style Feature 4 */}
                                <Td p={2} borderColor="gray.300" bg="green.50" whiteSpace="normal" wordBreak="break-word">
                                  <Textarea
                                    size="sm"
                                    fontSize="xs"
                                    w="150px"
                                    minH="80px"
                                    value={product.styleFeature4}
                                    onChange={(e) => handleProductChange(index, 'styleFeature4', e.target.value)}
                                    placeholder="Example: Sheer sleeves (word limit: 8-10)"
                                    rows={3}
                                    bg="white"
                                    border="1px solid"
                                    borderColor="gray.300"
                                    _hover={{ borderColor: "gray.400" }}
                                    _focus={{ borderColor: "blue.500", boxShadow: "0 0 0 1px blue.500" }}
                                    resize="vertical"
                                  />
                                </Td>

                                {/* Style Feature 5 */}
                                <Td p={2} borderColor="gray.300" bg="green.50" whiteSpace="normal" wordBreak="break-word">
                                  <Textarea
                                    size="sm"
                                    fontSize="xs"
                                    w="150px"
                                    minH="80px"
                                    value={product.styleFeature5}
                                    onChange={(e) => handleProductChange(index, 'styleFeature5', e.target.value)}
                                    placeholder="Example: Maxi length (word limit: 8-10)"
                                    rows={3}
                                    bg="white"
                                    border="1px solid"
                                    borderColor="gray.300"
                                    _hover={{ borderColor: "gray.400" }}
                                    _focus={{ borderColor: "blue.500", boxShadow: "0 0 0 1px blue.500" }}
                                    resize="vertical"
                                  />
                                </Td>

                                {/* Style Feature 6 */}
                                <Td p={2} borderColor="gray.300" bg="green.50" whiteSpace="normal" wordBreak="break-word">
                                  <Textarea
                                    size="sm"
                                    fontSize="xs"
                                    w="150px"
                                    minH="80px"
                                    value={product.styleFeature6}
                                    onChange={(e) => handleProductChange(index, 'styleFeature6', e.target.value)}
                                    placeholder="Example: Lined bodice"
                                    rows={3}
                                    bg="white"
                                    border="1px solid"
                                    borderColor="gray.300"
                                    _hover={{ borderColor: "gray.400" }}
                                    _focus={{ borderColor: "blue.500", boxShadow: "0 0 0 1px blue.500" }}
                                    resize="vertical"
                                  />
                                </Td>

                                {/* Material Composition */}
                                <Td p={2} borderColor="gray.300" bg="green.50" whiteSpace="normal" wordBreak="break-word">
                                  <Textarea
                                    size="sm"
                                    fontSize="xs"
                                    w="150px"
                                    minH="80px"
                                    value={product.materialComposition}
                                    onChange={(e) => handleProductChange(index, 'materialComposition', e.target.value)}
                                    placeholder="Example: 96% polyester / 4% elastane"
                                    rows={3}
                                    bg="white"
                                    border="1px solid"
                                    borderColor={errors[`materialComposition_${index}`] ? "red.500" : "gray.300"}
                                    _hover={{ borderColor: errors[`materialComposition_${index}`] ? "red.500" : "gray.400" }}
                                    _focus={{ borderColor: errors[`materialComposition_${index}`] ? "red.500" : "blue.500", boxShadow: errors[`materialComposition_${index}`] ? "0 0 0 1px red.500" : "0 0 0 1px blue.500" }}
                                    resize="vertical"
                                  />
                                </Td>

                                {/* Care Instructions */}
                                <Td p={2} borderColor="gray.300" bg="green.50">
                                  <Select
                                    size="sm"
                                    w="150px"
                                    value={product.careInstructions}
                                    onChange={(e) => handleProductChange(index, 'careInstructions', e.target.value)}
                                    placeholder="Example: Machine wash"
                                    bg="white"
                                    border="1px solid"
                                    borderColor={errors[`careInstructions_${index}`] ? "red.500" : "gray.300"}
                                    _hover={{ borderColor: errors[`careInstructions_${index}`] ? "red.500" : "gray.400" }}
                                    _focus={{ borderColor: errors[`careInstructions_${index}`] ? "red.500" : "blue.500", boxShadow: errors[`careInstructions_${index}`] ? "0 0 0 1px red.500" : "0 0 0 1px blue.500" }}
                                  >
                                    <option value="Machine wash">Machine wash</option>
                                    <option value="Hand wash only">Hand wash only</option>
                                    <option value="Dry clean only">Dry clean only</option>
                                    <option value="Machine wash cold">Machine wash cold</option>
                                    <option value="Do not bleach">Do not bleach</option>
                                    <option value="Spot clean">Spot clean</option>
                                  </Select>
                                </Td>

                                {/* Additional Care Notes */}
                                <Td p={2} borderColor="gray.300" bg="green.50" whiteSpace="normal" wordBreak="break-word">
                                  <Textarea
                                    size="sm"
                                    fontSize="xs"
                                    w="180px"
                                    minH="80px"
                                    value={product.additionalCareNotes}
                                    onChange={(e) => handleProductChange(index, 'additionalCareNotes', e.target.value)}
                                    placeholder="Example: Wash separately, lay flat to dry (10-15 words)"
                                    rows={3}
                                    bg="white"
                                    border="1px solid"
                                    borderColor="gray.300"
                                    _hover={{ borderColor: "gray.400" }}
                                    _focus={{ borderColor: "blue.500", boxShadow: "0 0 0 1px blue.500" }}
                                    resize="vertical"
                                  />
                                </Td>
                              </>
                            ) : (
                              <>
                                {/* Regular clothing fields - Show Style Features, Material, Care, and Fit fields */}
                                {!shouldShowHomeDecorFields(product.category) && !shouldShowCandlesFragrancesFields(product.category) && !shouldShowTablewareDiningFields(product.category) && (
                                  <>
                                    {/* Style Feature 1 */}
                                    <Td p={2} borderColor="gray.300" bg="green.50" whiteSpace="normal" wordBreak="break-word">
                                      <Textarea
                                        size="sm"
                                        fontSize="xs"
                                        w="150px"
                                        minH="80px"
                                        value={product.styleFeature1}
                                        onChange={(e) => handleProductChange(index, 'styleFeature1', e.target.value)}
                                        placeholder="Example: Crewneck (word limit: 8-10)"
                                        rows={3}
                                        bg="white"
                                        border="1px solid"
                                        borderColor={errors[`styleFeature1_${index}`] ? "red.500" : "gray.300"}
                                        _hover={{ borderColor: errors[`styleFeature1_${index}`] ? "red.500" : "gray.400" }}
                                        _focus={{ borderColor: errors[`styleFeature1_${index}`] ? "red.500" : "blue.500", boxShadow: errors[`styleFeature1_${index}`] ? "0 0 0 1px red.500" : "0 0 0 1px blue.500" }}
                                        resize="vertical"
                                      />
                                    </Td>

                                    {/* Style Feature 2 */}
                                    <Td p={2} borderColor="gray.300" bg="green.50" whiteSpace="normal" wordBreak="break-word">
                                      <Textarea
                                        size="sm"
                                        fontSize="xs"
                                        w="150px"
                                        minH="80px"
                                        value={product.styleFeature2}
                                        onChange={(e) => handleProductChange(index, 'styleFeature2', e.target.value)}
                                        placeholder="Example: Pulls over (word limit: 8-10)"
                                        rows={3}
                                        bg="white"
                                        border="1px solid"
                                        borderColor={errors[`styleFeature2_${index}`] ? "red.500" : "gray.300"}
                                        _hover={{ borderColor: errors[`styleFeature2_${index}`] ? "red.500" : "gray.400" }}
                                        _focus={{ borderColor: errors[`styleFeature2_${index}`] ? "red.500" : "blue.500", boxShadow: errors[`styleFeature2_${index}`] ? "0 0 0 1px red.500" : "0 0 0 1px blue.500" }}
                                        resize="vertical"
                                      />
                                    </Td>

                                    {/* Style Feature 3 */}
                                    <Td p={2} borderColor="gray.300" bg="green.50" whiteSpace="normal" wordBreak="break-word">
                                      <Textarea
                                        size="sm"
                                        fontSize="xs"
                                        w="150px"
                                        minH="80px"
                                        value={product.styleFeature3}
                                        onChange={(e) => handleProductChange(index, 'styleFeature3', e.target.value)}
                                        placeholder="Example: Hand-beaded detailing (word limit: 8-10)"
                                        rows={3}
                                        bg="white"
                                        border="1px solid"
                                        borderColor={errors[`styleFeature3_${index}`] ? "red.500" : "gray.300"}
                                        _hover={{ borderColor: errors[`styleFeature3_${index}`] ? "red.500" : "gray.400" }}
                                        _focus={{ borderColor: errors[`styleFeature3_${index}`] ? "red.500" : "blue.500", boxShadow: errors[`styleFeature3_${index}`] ? "0 0 0 1px red.500" : "0 0 0 1px blue.500" }}
                                        resize="vertical"
                                      />
                                    </Td>

                                    {/* Style Feature 4 */}
                                    <Td p={2} borderColor="gray.300" bg="green.50" whiteSpace="normal" wordBreak="break-word">
                                      <Textarea
                                        size="sm"
                                        fontSize="xs"
                                        w="150px"
                                        minH="80px"
                                        value={product.styleFeature4}
                                        onChange={(e) => handleProductChange(index, 'styleFeature4', e.target.value)}
                                        placeholder="Example: Sheer sleeves (word limit: 8-10)"
                                        rows={3}
                                        bg="white"
                                        border="1px solid"
                                        borderColor="gray.300"
                                        _hover={{ borderColor: "gray.400" }}
                                        _focus={{ borderColor: "blue.500", boxShadow: "0 0 0 1px blue.500" }}
                                        resize="vertical"
                                      />
                                    </Td>

                                    {/* Style Feature 5 */}
                                    <Td p={2} borderColor="gray.300" bg="green.50" whiteSpace="normal" wordBreak="break-word">
                                      <Textarea
                                        size="sm"
                                        fontSize="xs"
                                        w="150px"
                                        minH="80px"
                                        value={product.styleFeature5}
                                        onChange={(e) => handleProductChange(index, 'styleFeature5', e.target.value)}
                                        placeholder="Example: Maxi length (word limit: 8-10)"
                                        rows={3}
                                        bg="white"
                                        border="1px solid"
                                        borderColor="gray.300"
                                        _hover={{ borderColor: "gray.400" }}
                                        _focus={{ borderColor: "blue.500", boxShadow: "0 0 0 1px blue.500" }}
                                        resize="vertical"
                                      />
                                    </Td>

                                    {/* Style Feature 6 */}
                                    <Td p={2} borderColor="gray.300" bg="green.50" whiteSpace="normal" wordBreak="break-word">
                                      <Textarea
                                        size="sm"
                                        fontSize="xs"
                                        w="150px"
                                        minH="80px"
                                        value={product.styleFeature6}
                                        onChange={(e) => handleProductChange(index, 'styleFeature6', e.target.value)}
                                        placeholder="Example: Lined bodice"
                                        rows={3}
                                        bg="white"
                                        border="1px solid"
                                        borderColor="gray.300"
                                        _hover={{ borderColor: "gray.400" }}
                                        _focus={{ borderColor: "blue.500", boxShadow: "0 0 0 1px blue.500" }}
                                        resize="vertical"
                                      />
                                    </Td>

                                    {/* Material Composition */}
                                    <Td p={2} borderColor="gray.300" bg="green.50" whiteSpace="normal" wordBreak="break-word">
                                      <Textarea
                                        size="sm"
                                        fontSize="xs"
                                        w="150px"
                                        minH="80px"
                                        value={product.materialComposition}
                                        onChange={(e) => handleProductChange(index, 'materialComposition', e.target.value)}
                                        placeholder="Example: 96% polyester / 4% elastane"
                                        rows={3}
                                        bg="white"
                                        border="1px solid"
                                        borderColor={errors[`materialComposition_${index}`] ? "red.500" : "gray.300"}
                                        _hover={{ borderColor: errors[`materialComposition_${index}`] ? "red.500" : "gray.400" }}
                                        _focus={{ borderColor: errors[`materialComposition_${index}`] ? "red.500" : "blue.500", boxShadow: errors[`materialComposition_${index}`] ? "0 0 0 1px red.500" : "0 0 0 1px blue.500" }}
                                        resize="vertical"
                                      />
                                    </Td>

                                    {/* Care Instructions */}
                                    <Td p={2} borderColor="gray.300" bg="green.50">
                                      <Select
                                        size="sm"
                                        w="150px"
                                        value={product.careInstructions}
                                        onChange={(e) => handleProductChange(index, 'careInstructions', e.target.value)}
                                        placeholder="Example: Machine wash"
                                        bg="white"
                                        border="1px solid"
                                        borderColor={errors[`careInstructions_${index}`] ? "red.500" : "gray.300"}
                                        _hover={{ borderColor: errors[`careInstructions_${index}`] ? "red.500" : "gray.400" }}
                                        _focus={{ borderColor: errors[`careInstructions_${index}`] ? "red.500" : "blue.500", boxShadow: errors[`careInstructions_${index}`] ? "0 0 0 1px red.500" : "0 0 0 1px blue.500" }}
                                      >
                                        <option value="Machine wash">Machine wash</option>
                                        <option value="Hand wash only">Hand wash only</option>
                                        <option value="Dry clean only">Dry clean only</option>
                                        <option value="Machine wash cold">Machine wash cold</option>
                                        <option value="Do not bleach">Do not bleach</option>
                                        <option value="Spot clean">Spot clean</option>
                                      </Select>
                                    </Td>

                                    {/* Additional Care Notes */}
                                    <Td p={2} borderColor="gray.300" bg="green.50" whiteSpace="normal" wordBreak="break-word">
                                      <Textarea
                                        size="sm"
                                        fontSize="xs"
                                        w="180px"
                                        minH="80px"
                                        value={product.additionalCareNotes}
                                        onChange={(e) => handleProductChange(index, 'additionalCareNotes', e.target.value)}
                                        placeholder="Example: Wash separately, lay flat to dry (10-15 words)"
                                        rows={3}
                                        bg="white"
                                        border="1px solid"
                                        borderColor="gray.300"
                                        _hover={{ borderColor: "gray.400" }}
                                        _focus={{ borderColor: "blue.500", boxShadow: "0 0 0 1px blue.500" }}
                                        resize="vertical"
                                      />
                                    </Td>

                                    {/* Fit Type */}
                                    <Td p={2} borderColor="gray.300" bg="green.50">
                                  <Select
                                    size="sm"
                                    w="150px"
                                    value={product.fitType}
                                    onChange={(e) => handleProductChange(index, 'fitType', e.target.value)}
                                    placeholder="Example: True to size"
                                    bg="white"
                                    border="1px solid"
                                    borderColor={errors[`fitType_${index}`] ? "red.500" : "gray.300"}
                                    _hover={{ borderColor: errors[`fitType_${index}`] ? "red.500" : "gray.400" }}
                                    _focus={{ borderColor: errors[`fitType_${index}`] ? "red.500" : "blue.500", boxShadow: errors[`fitType_${index}`] ? "0 0 0 1px red.500" : "0 0 0 1px blue.500" }}
                                  >
                                    <option value="True to size">True to size</option>
                                    <option value="Runs small - order one size up">Runs small - order one size up</option>
                                    <option value="Runs large - order one size down">Runs large - order one size down</option>
                                    <option value="Oversized fit">Oversized fit</option>
                                    <option value="Fitted/tailored">Fitted/tailored</option>
                                  </Select>
                                </Td>

                                {/* Model Wearing Size */}
                                <Td p={2} borderColor="gray.300" bg="green.50">
                                  <Select
                                    size="sm"
                                    w="130px"
                                    value={product.modelWearingSize}
                                    onChange={(e) => {
                                      handleProductChange(index, 'modelWearingSize', e.target.value);
                                      // Clear age range if "Others" is not selected
                                      if (e.target.value !== 'Others') {
                                        handleProductChange(index, 'modelAgeRange', '');
                                      }
                                    }}
                                    placeholder="Example: Model is wearing a size small"
                                    bg="white"
                                    border="1px solid"
                                    borderColor="gray.300"
                                    _hover={{ borderColor: "gray.400" }}
                                    _focus={{ borderColor: "blue.500", boxShadow: "0 0 0 1px blue.500" }}
                                  >
                                    <option value="x-small">x-small</option>
                                    <option value="small">small</option>
                                    <option value="medium">medium</option>
                                    <option value="large">large</option>
                                    <option value="x-large">x-large</option>
                                    <option value="xx-large">xx-large</option>
                                    <option value="One Size">One Size</option>
                                    <option value="Others">Others</option>
                                  </Select>
                                </Td>

                                {/* Model Age Range - Show only if Model Wearing Size is "Others" */}
                                {product.modelWearingSize === 'Others' && (
                                  <Td p={2} borderColor="gray.300" bg="green.50">
                                    <Select
                                      size="sm"
                                      w="130px"
                                      value={product.modelAgeRange}
                                      onChange={(e) => handleProductChange(index, 'modelAgeRange', e.target.value)}
                                      placeholder="Select age range"
                                      bg="white"
                                      border="1px solid"
                                      borderColor="gray.300"
                                      _hover={{ borderColor: "gray.400" }}
                                      _focus={{ borderColor: "blue.500", boxShadow: "0 0 0 1px blue.500" }}
                                    >
                                      <option value="0-6 months">0-6 months</option>
                                      <option value="6-12 months">6-12 months</option>
                                      <option value="12-18 months">12-18 months</option>
                                      <option value="18-24 months">18-24 months</option>
                                      <option value="2-3 years">2-3 years</option>
                                      <option value="3-4 years">3-4 years</option>
                                      <option value="4-5 years">4-5 years</option>
                                      <option value="5-6 years">5-6 years</option>
                                      <option value="6-7 years">6-7 years</option>
                                      <option value="7-8 years">7-8 years</option>
                                      <option value="8-9 years">8-9 years</option>
                                      <option value="9-10 years">9-10 years</option>
                                      <option value="10-11 years">10-11 years</option>
                                      <option value="11-12 years">11-12 years</option>
                                      <option value="12-13 years">12-13 years</option>
                                      <option value="13-14 years">13-14 years</option>
                                    </Select>
                                  </Td>
                                )}

                                {/* Empty cell if Model Wearing Size is not "Others" but header is shown */}
                                {product.modelWearingSize !== 'Others' && products.some(p => p.modelWearingSize === 'Others') && (
                                  <Td p={2} borderColor="gray.300" bg="gray.100"></Td>
                                )}

                                {/* Model Height */}
                                <Td p={2} borderColor="gray.300" bg="green.50">
                                  <NumberInput
                                    size="sm"
                                    w="150px"
                                    value={product.modelHeight}
                                    onChange={(value) => handleProductChange(index, 'modelHeight', value)}
                                    min={0}
                                    bg="white"
                                  >
                                    <NumberInputField
                                      placeholder="Example: 175"
                                      border="1px solid"
                                      borderColor="gray.300"
                                      _hover={{ borderColor: "gray.400" }}
                                      _focus={{ borderColor: "blue.500", boxShadow: "0 0 0 1px blue.500" }}
                                    />
                                  </NumberInput>
                                </Td>

                                {/* Additional Fit Notes */}
                                <Td p={2} borderColor="gray.300" bg="green.50" whiteSpace="normal" wordBreak="break-word">
                                  <Textarea
                                    size="sm"
                                    fontSize="xs"
                                    w="200px"
                                    value={product.additionalFitNotes}
                                    onChange={(e) => handleProductChange(index, 'additionalFitNotes', e.target.value)}
                                    placeholder="Example: Flowy silhouette, relaxed through the body, hits at ankle (10-15 words)"
                                    rows={3}
                                    bg="white"
                                    border="1px solid"
                                    borderColor="gray.300"
                                    _hover={{ borderColor: "gray.400" }}
                                    _focus={{ borderColor: "blue.500", boxShadow: "0 0 0 1px blue.500" }}
                                    resize="vertical"
                                  />
                                </Td>
                              </>
                            )}
                              </>
                            )}
                            
                            {/* Home Decor specific fields */}
                            {shouldShowHomeDecorFields(product.category) && (
                              <>
                                {/* Color/Finish Options */}
                                <Td p={2} borderColor="gray.300" bg="green.50">
                                  <Input
                                    size="sm"
                                    w="180px"
                                    value={product.colorFinishOptions}
                                    onChange={(e) => handleProductChange(index, 'colorFinishOptions', e.target.value)}
                                    placeholder="Example: NO COLOR, Clear, White, Matte Black"
                                    bg="white"
                                    border="1px solid"
                                    borderColor={errors[`colorFinishOptions_${index}`] ? "red.500" : "gray.300"}
                                    _hover={{ borderColor: errors[`colorFinishOptions_${index}`] ? "red.500" : "gray.400" }}
                                    _focus={{ borderColor: errors[`colorFinishOptions_${index}`] ? "red.500" : "blue.500", boxShadow: errors[`colorFinishOptions_${index}`] ? "0 0 0 1px red.500" : "0 0 0 1px blue.500" }}
                                  />
                                </Td>

                                {/* Origin */}
                                <Td p={2} borderColor="gray.300" bg="green.50" whiteSpace="normal" wordBreak="break-word">
                                  <Textarea
                                    size="sm"
                                    fontSize="xs"
                                    w="180px"
                                    minH="80px"
                                    value={product.origin}
                                    onChange={(e) => handleProductChange(index, 'origin', e.target.value)}
                                    placeholder="Example: Designed in UAE / Made in Italy"
                                    rows={3}
                                    bg="white"
                                    border="1px solid"
                                    borderColor={errors[`origin_${index}`] ? "red.500" : "gray.300"}
                                    _hover={{ borderColor: errors[`origin_${index}`] ? "red.500" : "gray.400" }}
                                    _focus={{ borderColor: errors[`origin_${index}`] ? "red.500" : "blue.500", boxShadow: errors[`origin_${index}`] ? "0 0 0 1px red.500" : "0 0 0 1px blue.500" }}
                                    resize="vertical"
                                  />
                                </Td>

                                {/* Set Contents */}
                                <Td p={2} borderColor="gray.300" bg="green.50" whiteSpace="normal" wordBreak="break-word">
                                  <Textarea
                                    size="sm"
                                    fontSize="xs"
                                    w="180px"
                                    minH="80px"
                                    value={product.setContents}
                                    onChange={(e) => handleProductChange(index, 'setContents', e.target.value)}
                                    placeholder="Example: Set of 6 coasters with holder / Not applicable"
                                    rows={3}
                                    bg="white"
                                    border="1px solid"
                                    borderColor={errors[`setContents_${index}`] ? "red.500" : "gray.300"}
                                    _hover={{ borderColor: errors[`setContents_${index}`] ? "red.500" : "gray.400" }}
                                    _focus={{ borderColor: errors[`setContents_${index}`] ? "red.500" : "blue.500", boxShadow: errors[`setContents_${index}`] ? "0 0 0 1px red.500" : "0 0 0 1px blue.500" }}
                                    resize="vertical"
                                  />
                                </Td>

                                {/* Additional Information */}
                                <Td p={2} borderColor="gray.300" bg="green.50" whiteSpace="normal" wordBreak="break-word">
                                  <Textarea
                                    size="sm"
                                    fontSize="xs"
                                    w="180px"
                                    minH="80px"
                                    value={product.additionalInformation}
                                    onChange={(e) => handleProductChange(index, 'additionalInformation', e.target.value)}
                                    placeholder="Example: 600 thread count / Down fill / Memory foam"
                                    rows={3}
                                    bg="white"
                                    border="1px solid"
                                    borderColor="gray.300"
                                    _hover={{ borderColor: "gray.400" }}
                                    _focus={{ borderColor: "blue.500", boxShadow: "0 0 0 1px blue.500" }}
                                    resize="vertical"
                                  />
                                </Td>

                                {/* Limited Edition Info */}
                                <Td p={2} borderColor="gray.300" bg="green.50" whiteSpace="normal" wordBreak="break-word">
                                  <Textarea
                                    size="sm"
                                    fontSize="xs"
                                    w="180px"
                                    minH="80px"
                                    value={product.limitedEditionInfo}
                                    onChange={(e) => handleProductChange(index, 'limitedEditionInfo', e.target.value)}
                                    placeholder="Example: Limited edition of 100 / Numbered piece 45/100"
                                    rows={3}
                                    bg="white"
                                    border="1px solid"
                                    borderColor="gray.300"
                                    _hover={{ borderColor: "gray.400" }}
                                    _focus={{ borderColor: "blue.500", boxShadow: "0 0 0 1px blue.500" }}
                                    resize="vertical"
                                  />
                                </Td>

                                {/* Frame Material */}
                                <Td p={2} borderColor="gray.300" bg="green.50">
                                  <Input
                                    size="sm"
                                    w="180px"
                                    value={product.frameMaterial}
                                    onChange={(e) => handleProductChange(index, 'frameMaterial', e.target.value)}
                                    placeholder="Example: Brass frame with antiqued finish / Frameless"
                                    bg="white"
                                    border="1px solid"
                                    borderColor="gray.300"
                                    _hover={{ borderColor: "gray.400" }}
                                    _focus={{ borderColor: "blue.500", boxShadow: "0 0 0 1px blue.500" }}
                                  />
                                </Td>

                                {/* Photo/Art Size Capacity */}
                                <Td p={2} borderColor="gray.300" bg="green.50">
                                  <Input
                                    size="sm"
                                    w="180px"
                                    value={product.photoArtSizeCapacity}
                                    onChange={(e) => handleProductChange(index, 'photoArtSizeCapacity', e.target.value)}
                                    placeholder="Example: Fits 5in x 7in photo / A4 size"
                                    bg="white"
                                    border="1px solid"
                                    borderColor="gray.300"
                                    _hover={{ borderColor: "gray.400" }}
                                    _focus={{ borderColor: "blue.500", boxShadow: "0 0 0 1px blue.500" }}
                                  />
                                </Td>

                                {/* Orientation */}
                                <Td p={2} borderColor="gray.300" bg="green.50">
                                  <Select
                                    size="sm"
                                    w="180px"
                                    value={product.orientation}
                                    onChange={(e) => handleProductChange(index, 'orientation', e.target.value)}
                                    placeholder="Select orientation"
                                    bg="white"
                                    border="1px solid"
                                    borderColor="gray.300"
                                    _hover={{ borderColor: "gray.400" }}
                                    _focus={{ borderColor: "blue.500", boxShadow: "0 0 0 1px blue.500" }}
                                  >
                                    <option value="Portrait">Portrait</option>
                                    <option value="Landscape">Landscape</option>
                                    <option value="Both (can hang either way)">Both (can hang either way)</option>
                                    <option value="Tabletop only">Tabletop only</option>
                                  </Select>
                                </Td>

                                {/* Mounting Type */}
                                <Td p={2} borderColor="gray.300" bg="green.50">
                                  <Select
                                    size="sm"
                                    w="180px"
                                    value={product.mountingType}
                                    onChange={(e) => handleProductChange(index, 'mountingType', e.target.value)}
                                    placeholder="Select mounting type"
                                    bg="white"
                                    border="1px solid"
                                    borderColor="gray.300"
                                    _hover={{ borderColor: "gray.400" }}
                                    _focus={{ borderColor: "blue.500", boxShadow: "0 0 0 1px blue.500" }}
                                  >
                                    <option value="Wall-mounted">Wall-mounted</option>
                                    <option value="Freestanding">Freestanding</option>
                                    <option value="Both wall & tabletop">Both wall & tabletop</option>
                                    <option value="Tabletop only">Tabletop only</option>
                                  </Select>
                                </Td>
                              </>
                            )}
                            
                            {/* Candles & Fragrances specific fields */}
                            {shouldShowCandlesFragrancesFields(product.category) && (
                              <>
                                {/* Scent/Fragrance */}
                                <Td p={2} borderColor="gray.300" bg="green.50" whiteSpace="normal" wordBreak="break-word">
                                  <Textarea
                                    size="sm"
                                    fontSize="xs"
                                    w="180px"
                                    minH="80px"
                                    value={product.scentFragrance}
                                    onChange={(e) => handleProductChange(index, 'scentFragrance', e.target.value)}
                                    placeholder="Example: Saffron Kozani - Spicy, Vibrant / Lavender & Vanilla"
                                    rows={3}
                                    bg="white"
                                    border="1px solid"
                                    borderColor="gray.300"
                                    _hover={{ borderColor: "gray.400" }}
                                    _focus={{ borderColor: "blue.500", boxShadow: "0 0 0 1px blue.500" }}
                                    resize="vertical"
                                  />
                                </Td>

                                {/* Color/Finish Options */}
                                <Td p={2} borderColor="gray.300" bg="green.50">
                                  <Input
                                    size="sm"
                                    w="180px"
                                    value={product.colorFinishOptions}
                                    onChange={(e) => handleProductChange(index, 'colorFinishOptions', e.target.value)}
                                    placeholder="Example: NO COLOR / Amber glass / White wax"
                                    bg="white"
                                    border="1px solid"
                                    borderColor={errors[`colorFinishOptions_${index}`] ? "red.500" : "gray.300"}
                                    _hover={{ borderColor: errors[`colorFinishOptions_${index}`] ? "red.500" : "gray.400" }}
                                    _focus={{ borderColor: errors[`colorFinishOptions_${index}`] ? "red.500" : "blue.500", boxShadow: errors[`colorFinishOptions_${index}`] ? "0 0 0 1px red.500" : "0 0 0 1px blue.500" }}
                                  />
                                </Td>

                                {/* Origin */}
                                <Td p={2} borderColor="gray.300" bg="green.50" whiteSpace="normal" wordBreak="break-word">
                                  <Textarea
                                    size="sm"
                                    fontSize="xs"
                                    w="180px"
                                    minH="80px"
                                    value={product.origin}
                                    onChange={(e) => handleProductChange(index, 'origin', e.target.value)}
                                    placeholder="Example: Designed in UAE / Made in Italy"
                                    rows={3}
                                    bg="white"
                                    border="1px solid"
                                    borderColor={errors[`origin_${index}`] ? "red.500" : "gray.300"}
                                    _hover={{ borderColor: errors[`origin_${index}`] ? "red.500" : "gray.400" }}
                                    _focus={{ borderColor: errors[`origin_${index}`] ? "red.500" : "blue.500", boxShadow: errors[`origin_${index}`] ? "0 0 0 1px red.500" : "0 0 0 1px blue.500" }}
                                    resize="vertical"
                                  />
                                </Td>

                                {/* Capacity */}
                                <Td p={2} borderColor="gray.300" bg="green.50">
                                  <Input
                                    size="sm"
                                    w="180px"
                                    value={product.capacity}
                                    onChange={(e) => handleProductChange(index, 'capacity', e.target.value)}
                                    placeholder="Example: 200ml / 100ml / 50ml"
                                    bg="white"
                                    border="1px solid"
                                    borderColor="gray.300"
                                    _hover={{ borderColor: "gray.400" }}
                                    _focus={{ borderColor: "blue.500", boxShadow: "0 0 0 1px blue.500" }}
                                  />
                                </Td>

                                {/* Set Contents */}
                                <Td p={2} borderColor="gray.300" bg="green.50">
                                  <Input
                                    size="sm"
                                    w="180px"
                                    value={product.setContents}
                                    onChange={(e) => handleProductChange(index, 'setContents', e.target.value)}
                                    placeholder="Example: Includes candle, diffuser, and room spray"
                                    bg="white"
                                    border="1px solid"
                                    borderColor="gray.300"
                                    _hover={{ borderColor: "gray.400" }}
                                    _focus={{ borderColor: "blue.500", boxShadow: "0 0 0 1px blue.500" }}
                                  />
                                </Td>

                                {/* Burning Duration */}
                                <Td p={2} borderColor="gray.300" bg="green.50" whiteSpace="normal" wordBreak="break-word">
                                  <Textarea
                                    size="sm"
                                    fontSize="xs"
                                    w="180px"
                                    minH="80px"
                                    value={product.burningDuration}
                                    onChange={(e) => handleProductChange(index, 'burningDuration', e.target.value)}
                                    placeholder="Example: Approximately 45 hours / 50-60 hours"
                                    rows={3}
                                    bg="white"
                                    border="1px solid"
                                    borderColor="gray.300"
                                    _hover={{ borderColor: "gray.400" }}
                                    _focus={{ borderColor: "blue.500", boxShadow: "0 0 0 1px blue.500" }}
                                    resize="vertical"
                                  />
                                </Td>

                                {/* Non-Candle Product Type */}
                                <Td p={2} borderColor="gray.300" bg="green.50" whiteSpace="normal" wordBreak="break-word">
                                  <Textarea
                                    size="sm"
                                    fontSize="xs"
                                    w="180px"
                                    minH="80px"
                                    value={product.nonCandleProductType}
                                    onChange={(e) => handleProductChange(index, 'nonCandleProductType', e.target.value)}
                                    placeholder="Example: Reed diffuser / Room spray / Lasts up to 3 months"
                                    rows={3}
                                    bg="white"
                                    border="1px solid"
                                    borderColor="gray.300"
                                    _hover={{ borderColor: "gray.400" }}
                                    _focus={{ borderColor: "blue.500", boxShadow: "0 0 0 1px blue.500" }}
                                    resize="vertical"
                                  />
                                </Td>

                                {/* Usage Instructions */}
                                <Td p={2} borderColor="gray.300" bg="green.50" whiteSpace="normal" wordBreak="break-word">
                                  <Textarea
                                    size="sm"
                                    fontSize="xs"
                                    w="180px"
                                    minH="80px"
                                    value={product.usageInstructions}
                                    onChange={(e) => handleProductChange(index, 'usageInstructions', e.target.value)}
                                    placeholder="Example: Insert reeds into oil, flip weekly / Spray 2-3 times in room"
                                    rows={3}
                                    bg="white"
                                    border="1px solid"
                                    borderColor="gray.300"
                                    _hover={{ borderColor: "gray.400" }}
                                    _focus={{ borderColor: "blue.500", boxShadow: "0 0 0 1px blue.500" }}
                                    resize="vertical"
                                  />
                                </Td>

                                {/* Safety Warnings */}
                                <Td p={2} borderColor="gray.300" bg="green.50" whiteSpace="normal" wordBreak="break-word">
                                  <Textarea
                                    size="sm"
                                    fontSize="xs"
                                    w="180px"
                                    minH="80px"
                                    value={product.safetyWarnings}
                                    onChange={(e) => handleProductChange(index, 'safetyWarnings', e.target.value)}
                                    placeholder="Example: Never leave burning candle unattended. Keep away from children and pets."
                                    rows={3}
                                    bg="white"
                                    border="1px solid"
                                    borderColor={errors[`safetyWarnings_${index}`] ? "red.500" : "gray.300"}
                                    _hover={{ borderColor: errors[`safetyWarnings_${index}`] ? "red.500" : "gray.400" }}
                                    _focus={{ borderColor: errors[`safetyWarnings_${index}`] ? "red.500" : "blue.500", boxShadow: errors[`safetyWarnings_${index}`] ? "0 0 0 1px red.500" : "0 0 0 1px blue.500" }}
                                    resize="vertical"
                                  />
                                </Td>
                              </>
                            )}
                            
                            {/* Tableware / Dining specific fields */}
                            {shouldShowTablewareDiningFields(product.category) && (
                              <>
                                {/* Color/Finish Options */}
                                <Td p={2} borderColor="gray.300" bg="green.50">
                                  <Input
                                    size="sm"
                                    w="180px"
                                    value={product.colorFinishOptions}
                                    onChange={(e) => handleProductChange(index, 'colorFinishOptions', e.target.value)}
                                    placeholder="Example: NO COLOR / Clear / White / Matte Black"
                                    bg="white"
                                    border="1px solid"
                                    borderColor={errors[`colorFinishOptions_${index}`] ? "red.500" : "gray.300"}
                                    _hover={{ borderColor: errors[`colorFinishOptions_${index}`] ? "red.500" : "gray.400" }}
                                    _focus={{ borderColor: errors[`colorFinishOptions_${index}`] ? "red.500" : "blue.500", boxShadow: errors[`colorFinishOptions_${index}`] ? "0 0 0 1px red.500" : "0 0 0 1px blue.500" }}
                                  />
                                </Td>

                                {/* Origin */}
                                <Td p={2} borderColor="gray.300" bg="green.50" whiteSpace="normal" wordBreak="break-word">
                                  <Textarea
                                    size="sm"
                                    fontSize="xs"
                                    w="180px"
                                    minH="80px"
                                    value={product.origin}
                                    onChange={(e) => handleProductChange(index, 'origin', e.target.value)}
                                    placeholder="Example: Designed in UAE / Made in Italy"
                                    rows={3}
                                    bg="white"
                                    border="1px solid"
                                    borderColor={errors[`origin_${index}`] ? "red.500" : "gray.300"}
                                    _hover={{ borderColor: errors[`origin_${index}`] ? "red.500" : "gray.400" }}
                                    _focus={{ borderColor: errors[`origin_${index}`] ? "red.500" : "blue.500", boxShadow: errors[`origin_${index}`] ? "0 0 0 1px red.500" : "0 0 0 1px blue.500" }}
                                    resize="vertical"
                                  />
                                </Td>

                                {/* Capacity */}
                                <Td p={2} borderColor="gray.300" bg="green.50">
                                  <Input
                                    size="sm"
                                    w="180px"
                                    value={product.tablewareCapacity}
                                    onChange={(e) => handleProductChange(index, 'tablewareCapacity', e.target.value)}
                                    placeholder="Example: About 400ml / 2.5 liters"
                                    bg="white"
                                    border="1px solid"
                                    borderColor="gray.300"
                                    _hover={{ borderColor: "gray.400" }}
                                    _focus={{ borderColor: "blue.500", boxShadow: "0 0 0 1px blue.500" }}
                                  />
                                </Td>

                                {/* Set Contents */}
                                <Td p={2} borderColor="gray.300" bg="green.50" whiteSpace="normal" wordBreak="break-word">
                                  <Textarea
                                    size="sm"
                                    fontSize="xs"
                                    w="180px"
                                    minH="80px"
                                    value={product.setContents}
                                    onChange={(e) => handleProductChange(index, 'setContents', e.target.value)}
                                    placeholder="Example: Set of 4: four tumblers / Includes 4 dinner plates"
                                    rows={3}
                                    bg="white"
                                    border="1px solid"
                                    borderColor="gray.300"
                                    _hover={{ borderColor: "gray.400" }}
                                    _focus={{ borderColor: "blue.500", boxShadow: "0 0 0 1px blue.500" }}
                                    resize="vertical"
                                  />
                                </Td>

                                {/* Safety Warnings */}
                                <Td p={2} borderColor="gray.300" bg="green.50" whiteSpace="normal" wordBreak="break-word">
                                  <Textarea
                                    size="sm"
                                    fontSize="xs"
                                    w="180px"
                                    minH="80px"
                                    value={product.safetyWarnings}
                                    onChange={(e) => handleProductChange(index, 'safetyWarnings', e.target.value)}
                                    placeholder="Example: Handle with care - fragile / Hot surface - use caution"
                                    rows={3}
                                    bg="white"
                                    border="1px solid"
                                    borderColor={errors[`safetyWarnings_${index}`] ? "red.500" : "gray.300"}
                                    _hover={{ borderColor: errors[`safetyWarnings_${index}`] ? "red.500" : "gray.400" }}
                                    _focus={{ borderColor: errors[`safetyWarnings_${index}`] ? "red.500" : "blue.500", boxShadow: errors[`safetyWarnings_${index}`] ? "0 0 0 1px red.500" : "0 0 0 1px blue.500" }}
                                    resize="vertical"
                                  />
                                </Td>

                                {/* Food Safe Certification */}
                                <Td p={2} borderColor="gray.300" bg="green.50">
                                  <Input
                                    size="sm"
                                    w="180px"
                                    value={product.foodSafeCertification}
                                    onChange={(e) => handleProductChange(index, 'foodSafeCertification', e.target.value)}
                                    placeholder="Example: Food-safe materials / BPA-free / FDA approved"
                                    bg="white"
                                    border="1px solid"
                                    borderColor="gray.300"
                                    _hover={{ borderColor: "gray.400" }}
                                    _focus={{ borderColor: "blue.500", boxShadow: "0 0 0 1px blue.500" }}
                                  />
                                </Td>
                              </>
                            )}
                          </>
                        )}

                        {/* Empty cells if category doesn't match but headers are shown (for remaining Product Details fields) */}
                        {!shouldShowProductDetailsFields(product.category) && products.some(p => shouldShowProductDetailsFields(p.category)) && (
                          <>
                            {/* Show 15 empty cells if any product is a bag category, otherwise 13 */}
                            {products.some(p => shouldShowBagFields(p.category)) ? (
                              <>
                                <Td p={2} borderColor="gray.300" bg="gray.100"></Td>
                                <Td p={2} borderColor="gray.300" bg="gray.100"></Td>
                                <Td p={2} borderColor="gray.300" bg="gray.100"></Td>
                                <Td p={2} borderColor="gray.300" bg="gray.100"></Td>
                                <Td p={2} borderColor="gray.300" bg="gray.100"></Td>
                                <Td p={2} borderColor="gray.300" bg="gray.100"></Td>
                                <Td p={2} borderColor="gray.300" bg="gray.100"></Td>
                                <Td p={2} borderColor="gray.300" bg="gray.100"></Td>
                                <Td p={2} borderColor="gray.300" bg="gray.100"></Td>
                                <Td p={2} borderColor="gray.300" bg="gray.100"></Td>
                                <Td p={2} borderColor="gray.300" bg="gray.100"></Td>
                                <Td p={2} borderColor="gray.300" bg="gray.100"></Td>
                                <Td p={2} borderColor="gray.300" bg="gray.100"></Td>
                                <Td p={2} borderColor="gray.300" bg="gray.100"></Td>
                                <Td p={2} borderColor="gray.300" bg="gray.100"></Td>
                              </>
                            ) : (
                              <>
                                <Td p={2} borderColor="gray.300" bg="gray.100"></Td>
                                <Td p={2} borderColor="gray.300" bg="gray.100"></Td>
                                <Td p={2} borderColor="gray.300" bg="gray.100"></Td>
                                <Td p={2} borderColor="gray.300" bg="gray.100"></Td>
                                <Td p={2} borderColor="gray.300" bg="gray.100"></Td>
                                <Td p={2} borderColor="gray.300" bg="gray.100"></Td>
                                <Td p={2} borderColor="gray.300" bg="gray.100"></Td>
                                <Td p={2} borderColor="gray.300" bg="gray.100"></Td>
                                <Td p={2} borderColor="gray.300" bg="gray.100"></Td>
                                <Td p={2} borderColor="gray.300" bg="gray.100"></Td>
                                <Td p={2} borderColor="gray.300" bg="gray.100"></Td>
                                <Td p={2} borderColor="gray.300" bg="gray.100"></Td>
                                <Td p={2} borderColor="gray.300" bg="gray.100"></Td>
                              </>
                            )}
                          </>
                        )}

                        {/* Empty cells for Home Decor fields if headers are shown but product doesn't match */}
                        {!shouldShowHomeDecorFields(product.category) && products.some(p => shouldShowHomeDecorFields(p.category)) && (
                          <>
                            <Td p={2} borderColor="gray.300" bg="gray.100"></Td>
                            <Td p={2} borderColor="gray.300" bg="gray.100"></Td>
                            <Td p={2} borderColor="gray.300" bg="gray.100"></Td>
                            <Td p={2} borderColor="gray.300" bg="gray.100"></Td>
                            <Td p={2} borderColor="gray.300" bg="gray.100"></Td>
                            <Td p={2} borderColor="gray.300" bg="gray.100"></Td>
                            <Td p={2} borderColor="gray.300" bg="gray.100"></Td>
                            <Td p={2} borderColor="gray.300" bg="gray.100"></Td>
                            <Td p={2} borderColor="gray.300" bg="gray.100"></Td>
                          </>
                        )}

                        {/* Empty cells for Candles & Fragrances fields if headers are shown but product doesn't match */}
                        {!shouldShowCandlesFragrancesFields(product.category) && products.some(p => shouldShowCandlesFragrancesFields(p.category)) && (
                          <>
                            <Td p={2} borderColor="gray.300" bg="gray.100"></Td>
                            <Td p={2} borderColor="gray.300" bg="gray.100"></Td>
                            <Td p={2} borderColor="gray.300" bg="gray.100"></Td>
                            <Td p={2} borderColor="gray.300" bg="gray.100"></Td>
                            <Td p={2} borderColor="gray.300" bg="gray.100"></Td>
                            <Td p={2} borderColor="gray.300" bg="gray.100"></Td>
                            <Td p={2} borderColor="gray.300" bg="gray.100"></Td>
                            <Td p={2} borderColor="gray.300" bg="gray.100"></Td>
                            <Td p={2} borderColor="gray.300" bg="gray.100"></Td>
                          </>
                        )}

                        {/* Empty cells for Tableware / Dining fields if headers are shown but product doesn't match */}
                        {!shouldShowTablewareDiningFields(product.category) && products.some(p => shouldShowTablewareDiningFields(p.category)) && (
                          <>
                            <Td p={2} borderColor="gray.300" bg="gray.100"></Td>
                            <Td p={2} borderColor="gray.300" bg="gray.100"></Td>
                            <Td p={2} borderColor="gray.300" bg="gray.100"></Td>
                            <Td p={2} borderColor="gray.300" bg="gray.100"></Td>
                            <Td p={2} borderColor="gray.300" bg="gray.100"></Td>
                            <Td p={2} borderColor="gray.300" bg="gray.100"></Td>
                          </>
                        )}

                        {!isShopify && (
                          <Td p={2} borderColor="gray.300">
                            <Select
                              size="sm"
                              w="110px"
                              value={product.size}
                              onChange={(e) => handleProductChange(index, 'size', e.target.value)}
                              placeholder="Select size"
                              isDisabled={product.category === 'Other Accessories' && !product.subcategory}
                              bg="white"
                              border="1px solid"
                              borderColor="gray.300"
                              _hover={{ borderColor: "gray.400" }}
                              _focus={{ borderColor: "blue.500", boxShadow: "0 0 0 1px blue.500" }}
                            >
                              {getSizeOptions(product.category, product.subcategory).map(size => (
                                <option key={size} value={size}>{size}</option>
                              ))}
                            </Select>
                          </Td>
                        )}

                        {!isShopify && (
                          <Td p={2} borderColor="gray.300">
                            <Select
                              size="sm"
                              w="130px"
                              value={product.color}
                              onChange={(e) => handleProductChange(index, 'color', e.target.value)}
                              placeholder="Select color"
                              bg="white"
                              border="1px solid"
                              borderColor="gray.300"
                              _hover={{ borderColor: "gray.400" }}
                              _focus={{ borderColor: "blue.500", boxShadow: "0 0 0 1px blue.500" }}
                            >
                              {getColorOptions(product.category, product.subcategory).map(color => (
                                <option key={color} value={color}>{color}</option>
                              ))}
                            </Select>
                          </Td>
                        )}

                        {!isShopify && (
                          <Td p={2} borderColor="gray.300">
                            <Select
                              size="sm"
                              w="140px"
                              value={product.pattern}
                              onChange={(e) => handleProductChange(index, 'pattern', e.target.value)}
                              placeholder="Select pattern"
                              bg="white"
                              border="1px solid"
                              borderColor="gray.300"
                              _hover={{ borderColor: "gray.400" }}
                              _focus={{ borderColor: "blue.500", boxShadow: "0 0 0 1px blue.500" }}
                            >
                              {getPatternOptions(product.category).map(pattern => (
                                <option key={pattern} value={pattern}>{pattern}</option>
                              ))}
                            </Select>
                          </Td>
                        )}

                        <Td p={2} borderColor="gray.300">
                          <Input
                            size="sm"
                            w="70px"
                            value={product.weight}
                            onChange={(e) => handleProductChange(index, 'weight', e.target.value)}
                            placeholder="Weight"
                            bg="white"
                            border="1px solid"
                            borderColor="gray.300"
                            _hover={{ borderColor: "gray.400" }}
                            _focus={{ borderColor: "blue.500", boxShadow: "0 0 0 1px blue.500" }}
                          />
                        </Td>

                        <Td p={2} borderColor="gray.300">
                          <Input
                            size="sm"
                            w="70px"
                            value={product.length}
                            onChange={(e) => handleProductChange(index, 'length', e.target.value)}
                            placeholder="Length"
                            bg="white"
                            border="1px solid"
                            borderColor="gray.300"
                            _hover={{ borderColor: "gray.400" }}
                            _focus={{ borderColor: "blue.500", boxShadow: "0 0 0 1px blue.500" }}
                          />
                        </Td>

                        <Td p={2} borderColor="gray.300">
                          <Input
                            size="sm"
                            w="70px"
                            value={product.width}
                            onChange={(e) => handleProductChange(index, 'width', e.target.value)}
                            placeholder="Width"
                            bg="white"
                            border="1px solid"
                            borderColor="gray.300"
                            _hover={{ borderColor: "gray.400" }}
                            _focus={{ borderColor: "blue.500", boxShadow: "0 0 0 1px blue.500" }}
                          />
                        </Td>

                        <Td p={2} borderColor="gray.300">
                          <Input
                            size="sm"
                            w="70px"
                            value={product.height}
                            onChange={(e) => handleProductChange(index, 'height', e.target.value)}
                            placeholder="Height"
                            bg="white"
                            border="1px solid"
                            borderColor="gray.300"
                            _hover={{ borderColor: "gray.400" }}
                            _focus={{ borderColor: "blue.500", boxShadow: "0 0 0 1px blue.500" }}
                          />
                        </Td>

                        {!isShopify && (
                          <>
                            <Td p={2} borderColor="gray.300">
                              <Input
                                size="sm"
                                w="150px"
                                value={product.mainImage}
                                onChange={(e) => handleProductChange(index, 'mainImage', e.target.value)}
                                placeholder="main-image.jpg"
                                bg="white"
                                border="1px solid"
                                borderColor="gray.300"
                                _hover={{ borderColor: "gray.400" }}
                                _focus={{ borderColor: "blue.500", boxShadow: "0 0 0 1px blue.500" }}
                              />
                            </Td>

                            <Td p={2} borderColor="gray.300">
                              <Input
                                size="sm"
                                w="180px"
                                value={product.additionalImages}
                                onChange={(e) => handleProductChange(index, 'additionalImages', e.target.value)}
                                placeholder="image2.jpg, image3.jpg"
                                bg="white"
                                border="1px solid"
                                borderColor="gray.300"
                                _hover={{ borderColor: "gray.400" }}
                                _focus={{ borderColor: "blue.500", boxShadow: "0 0 0 1px blue.500" }}
                              />
                            </Td>

                            <Td p={2} borderColor="gray.300">
                              <Textarea
                                size="sm"
                                fontSize="xs"
                                w="180px"
                                value={product.notes}
                                onChange={(e) => handleProductChange(index, 'notes', e.target.value)}
                                placeholder="Additional notes"
                                rows={3}
                                bg="white"
                                border="1px solid"
                                borderColor="gray.300"
                                _hover={{ borderColor: "gray.400" }}
                                _focus={{ borderColor: "blue.500", boxShadow: "0 0 0 1px blue.500" }}
                              />
                            </Td>
                          </>
                        )}

                        <Td p={2} borderColor="gray.300">
                          <Select
                            size="sm"
                            w="140px"
                            value={product.packagingType}
                            onChange={(e) => handleProductChange(index, 'packagingType', e.target.value)}
                            placeholder="Select packaging"
                            bg="white"
                            border="1px solid"
                            borderColor="gray.300"
                            _hover={{ borderColor: "gray.400" }}
                            _focus={{ borderColor: "blue.500", boxShadow: "0 0 0 1px blue.500" }}
                          >
                            <option value="Box">Box</option>
                            <option value="Envelope">Envelope</option>
                            <option value="Poly Mailer">Poly Mailer</option>
                            <option value="Bubble Mailer">Bubble Mailer</option>
                            <option value="Padded Envelope">Padded Envelope</option>
                            <option value="Other">Other</option>
                          </Select>
                        </Td>
                      </Tr>
                    ))}
                  </Tbody>
                </Table>
              </Box>

              <Divider my={6} />

              {/* Instructions */}
              <Card variant="outline">
                <CardHeader>
                  <Heading size="md" color="black">Instructions for Brands:</Heading>
                </CardHeader>
                <CardBody>
                  <VStack align="start" spacing={2}>
                    <Text><strong>1.</strong> Use the toggle above to switch between Shopify and Non-Shopify modes</Text>
                    <Text><strong>2.</strong> Fill in the common information form above the table</Text>
                    <Text><strong>3.</strong> Select Category from dropdown - Subcategory will update automatically</Text>
                    <Text><strong>4.</strong> Select Inventory Availability - If "Made to Order" is selected, enter Lead Time in days</Text>
                    <Text><strong>5.</strong> Weight and dimensions (approximates) are required for shipping purposes</Text>
                    <Text><strong>6.</strong> Click "Add Product Row" to add more products</Text>
                    <Text><strong>7.</strong> Click "Import Excel" to load data from an existing file</Text>
                    <Text><strong>8.</strong> Click "Download Excel File" to save your completed data as XLSX format</Text>
                    <Text><strong>9.</strong> The downloaded file can be opened in Excel, LibreOffice, or Google Sheets</Text>
                  </VStack>
                </CardBody>
              </Card>
            </CardBody>
          </Card>
        </Container>
      </Box>
    </ChakraProvider>
  );
};

export default ProductOnboardingForm;
